import type { NamespaceDesktopWebTranslation } from '../../i18n-types';

const da_desktop_web = {
  eligiblePayers: {
    title: 'Godkendte forsikringsselskaber',
    subtitle:
      'Bekræft patientens forsikringsdækning, før du indsender en eConsult.',
    payerCount: '{count} {{forsikringsselskab|forsikringsselskaber}}',
  },
  activityTracker: {
    title: 'Du bliver snart logget ud',
    description:
      'Du har været inaktiv i næsten {amountOfMinutes} minutter. Flyt på din mus for at forblive logget ind.',
  },
  annotations: {
    annotationList: {
      noDataInTable: 'Ingen tilgængelige data',
      startAnnotating: 'Begynd annotering',
      tableHeader: {
        diagnosis: 'Diagnose',
        difficulty: 'Sværhedsgrad',
        location: 'Placering',
        status: 'Status',
      },
    },
    annotationsPage: {
      successfullySkippedCase: 'Du sprang denne case over',
      successfullyAnnotatedCase: 'Du annoterede denne case',
      somethingWentWrongCreatingAnnotation: 'Kunne ikke gemme annoteringen',
      leftSideContent: {
        annotations: 'Annoteringer',
        answers: 'Svar',
        backToList: 'Tilbage til case-liste',
        diagnosis: 'Diagnose',
        difficulty: 'Sværhedsgrad',
        fetchingFormerRaters: 'Henter tidligere bedømmere',
        formerRaters: 'Tidligere bedømmere',
        genderInTheirAge:
          '{gender|{Female: Kvinde, *: Mand}} i {pronoun|{Male: sine, Female: sine, *: sine}} {age}er',
        noFormerReviews: 'Ingen tidligere gennemgange',
      },
      roi: {
        button: 'SIO',
        title: 'SIO',
      },
      toolbar: {
        addTool: 'Tilføj {tool|{arrow:pile, pen:områder}}',
        annotationCriteriaLabel: 'Annoter {criteriaName}',
        annotations: 'Annoteringer',
        approve: 'Godkend',
        area: 'Område{{r}}',
        arrow: 'Pil{{e}}',
        arrowTool: 'Pil',
        cancel: 'Annuller',
        clickToAnnotate:
          'Klik på billedet for at tilføje nye {tool|{arrow:pile, pen:områder}}',
        clinicalImage: 'Overbliksbillede',
        collapseText: 'Færdig',
        criteriaMultiSelect: 'Vælg flere',
        criteriaPlaceholder: 'Søg efter øvrige kriterier',
        criteriaSearchLabel: 'Tilføj øvrige kriterier',
        criteriaSelectLabel: 'Kriterier for {diagnosisName}',
        dermoscopicImage: 'Dermatoskopiske billede',
        edit: 'Redigér',
        editorNoteLabel: 'Overordnet note',
        editorNotePlaceholder: 'Indtast note her...',
        editorNotePlaceholderReadonly: 'Ingen note',
        feedbackLabel: 'Feedback',
        feedbackNotePlaceholder: 'Indtast feedback her...',
        feedbackNotePlaceholderReadonly: 'Ingen feedback',
        markAsRead:
          '{markedAsRead|{markedAsRead:Læst, notMarkedAsRead:Markér som læst}}',
        penTool: 'Pen',
        requestChanges: 'Anmod om ændringer',
        save: 'Gem',
        skip: 'Spring over',
        title: 'Fremgang',
        unApprove: 'Fjern godkendelse',
      },
    },
    tags: {
      annotated: 'Annoteret',
      approved: 'Godkendt',
      changesRequested: 'Uløste kommentarer',
      disapproved: 'Ikke godkendt',
      newFeedback: 'Ny feedback',
      pending: 'Afventer',
      skipped: 'Sprunget over',
    },
  },
  administration: {
    navigationSidebar: {
      users: 'Brugere',
      eConsults: 'Henvisninger',
      title: 'Administration',
      specialists: 'Specialister',
      sites: 'Klinikker',
      networks: 'Netværk',
      federationSettings: 'Organisationsindstillinger',
      forms: 'Formularer',
      specialities: 'Specialer',
      announcements: 'Meddelelser',
      consents: 'Samtykker',
      globalLabel: 'Global',
    },
    federationSelector: {
      chooserTitle: 'Vælg en organisation',
      searchPlaceholder: 'Søg efter organisationer',
      noFederations: 'Ingen organisationer fundet',
      noSearchMatches: 'Ingen organisationer matcher din søgning',
      loadError: 'Organisationer kunne ikke indlæses',
      retry: 'Prøv igen',
    },
  },
  formsPage: {
    title: 'Formularskabeloner',
    slots: {
      'oral-triage': 'Oral læsion — triage',
      'oral-tele-assessment': 'Oral læsion — specialistvurdering',
      'cervical-triage': 'Cervixtilstand — triage',
      'cervical-tele-assessment': 'Cervixtilstand — specialistvurdering',
      'shared-response-review':
        'Oral læsion og cervixtilstand — vurdering af svar',
    },
    columns: {
      form: 'Formular',
      version: 'Version',
      formLanguageVersion: 'Formsprogsversion',
      lastPublished: 'Senest publiceret',
      updater: 'Opdateret af',
      actions: 'Handlinger',
    },
    notPublishedYet: 'Ikke publiceret endnu',
    noTemplates: 'Ingen registrerede formularskabeloner',
    upload: 'Upload JSON',
    uploadFor: 'Upload JSON for {form}',
    confirm: {
      title: 'Overskriv den publicerede skabelon?',
      description: 'Dette erstatter den aktuelt publicerede JSON for {form}.',
      action: 'Overskriv skabelon',
    },
    publishSuccess: '{form} blev publiceret.',
    loadError: 'Formularskabelonerne kunne ikke indlæses. Prøv igen.',
    errors: {
      malformedJson: 'Den valgte fil er ikke gyldig JSON: {message}',
      validationFailed: 'Skabelonen kunne ikke valideres:',
      requestTooLarge:
        'Den serialiserede upload-anmodning er for stor. Den skal være under 100 kB.',
      readFailed: 'Den valgte fil kunne ikke læses.',
      publishFailed: 'Formularskabelonen kunne ikke publiceres. Prøv igen.',
      alreadyCreated:
        'En anden nåede at publicere denne formular først. Listen er opdateret — upload igen for at publicere en ny version.',
      assignedElsewhere:
        'Publiceret, men en anden skabelon er allerede tildelt en af dens tilstandstyper for denne formular, så den blev ikke ændret.',
      assignFailed:
        'Publiceret, men den kunne ikke tildeles alle tilstandstyper. Upload igen for at prøve igen.',
    },
  },
  announcementsPage: {
    title: 'Meddelelser',
    description:
      'En bruger ser én meddelelse ad gangen: kritisk først, derefter advarsel, info og succes. Den nyeste vinder ved lighed.',
    create: 'Opret meddelelse',
    noData: 'Ingen meddelelser endnu',
    noMatches: 'Ingen meddelelser matcher filtrene',
    noDataDescription:
      'Meddelelser, du opretter for denne føderation, vises her.',
    loadError: 'Meddelelserne kunne ikke indlæses.',
    columns: {
      title: 'Titel',
      type: 'Type',
      status: 'Status',
      audience: 'Målgruppe',
      statistics: 'Statistik',
      period: 'Periode (UTC)',
    },
    allPages: 'Alle sider',
    types: {
      info: 'Info',
      warning: 'Advarsel',
      success: 'Succes',
      critical: 'Kritisk',
    },
    statuses: {
      draft: 'Kladde',
      scheduled: 'Planlagt',
      active: 'Aktiv',
      expired: 'Udløbet',
    },
    shadowed:
      'Skygges af “{title}” for en overlappende målgruppe, {period} (UTC)',
    audience: {
      allUsers: 'Alle brugere',
      federationManager: 'Føderationsansvarlig',
      roles: '{count} rolle{{r}}',
      jurisdictionalGroups: '{count} netværk',
      medicalUnits: '{count} klinik{{ker}}',
      rolesIn: '{roles} i {scopes}',
    },
    statistics: {
      estimatedReach: 'anslået rækkevidde',
      impressions: 'Visninger',
      uniqueUsers: 'Brugere',
      dismissals: 'Afvist',
      linkOpens: 'Linkklik',
      note: 'Tæller kun brugere med produktanalyse slået til.',
    },
    period: {
      untilRemoved: 'Indtil fjernet',
      from: 'Fra {date}',
      to: 'Til {date}',
    },
    actions: {
      edit: 'Redigér',
      duplicate: 'Duplikér',
      preview: 'Fuld forhåndsvisning',
      delete: 'Slet',
    },
    filters: {
      clear: 'Ryd',
      selected: 'Valgt',
      all: 'Alle',
    },
    deleteDialog: {
      title: 'Slet meddelelse?',
      description: '“{title}” fjernes fra listen og fra alle brugeres banner.',
      confirm: 'Slet',
      deleted: 'Meddelelsen er slettet',
      failed: 'Meddelelsen kunne ikke slettes.',
    },
    preview: {
      title: 'Fuld forhåndsvisning',
      description: 'Sådan ser brugerne meddelelsen øverst på hver side.',
    },
    sheet: {
      createTitle: 'Opret meddelelse',
      editTitle: 'Redigér meddelelse',
      steps: {
        target: 'Mål',
        audience: 'Målgruppe',
        content: 'Indhold',
        review: 'Gennemse',
      },
      federations: {
        label: 'Føderationer',
        selectAll: 'Vælg alle',
        helper: 'Hver valgt føderation får sin egen meddelelse.',
        readOnly:
          'En meddelelse bliver i den føderation, den blev oprettet til.',
      },
      audience: {
        everyone: 'Alle',
        narrow: 'Afgræns med roller og steder',
        allRoles: 'Alle roller',
        roles: 'Roller',
        scopes: 'Hvor',
        scopesHelper: 'Intet valgt betyder hele føderationen.',
        jurisdictionalGroups: 'Netværk',
        medicalUnits: 'Klinikker',
        searchJurisdictionalGroups: 'Søg i netværk',
        searchMedicalUnits: 'Søg i klinikker',
        removedScope: 'Findes ikke længere',
        loadError: 'Føderationens netværk og klinikker kunne ikke indlæses.',
      },
      content: {
        type: 'Type',
        typeDescriptions: {
          info: 'Generel information.',
          warning: 'Noget, brugerne skal planlægge efter.',
          success: 'Noget, der gik godt eller er løst.',
          critical: 'En aktiv klinisk eller systemmæssig risiko.',
        },
        typeHelper: 'Sætter farven, og om brugerne kan afvise den.',
        dismissible: 'Kan afvises',
        minimiseOnly: 'Kan kun minimeres',
        language: 'Indhold på {language}',
        languageHelper: 'Til {federations}.',
        title: 'Titel',
        body: 'Besked',
        linkLabel: 'Linktekst',
        linkUrl: 'Link-URL',
        linkHelper: 'Valgfrit. Et link kræver både en tekst og en URL.',
        linkLabelCount: '{count}/{max}',
        preview: 'Forhåndsvisning',
        previewNote: 'Sådan vil meddelelsen se ud, når den er udgivet.',
      },
      review: {
        schedule: 'Tidsplan',
        startsAt: 'Aktiv fra (UTC)',
        endsAt: 'Aktiv til (UTC)',
        date: 'Dato',
        time: 'Tid',
        pickDate: 'Vælg en dato',
        untilRemoved: 'Indtil fjernet (ingen slutdato)',
        summary: '{count} separat{{e}} meddelelse{{r}} oprettes:',
        summaryEdit: 'Meddelelsen hører til {federation}.',
        independent: 'Hver redigeres, slettes og tælles for sig.',
      },
      errors: {
        federations: 'Vælg mindst én føderation.',
        removedScope:
          'Fjern markeringen af de netværk og klinikker, der ikke længere findes.',
        narrowEmpty: 'Vælg mindst én rolle, ét netværk eller én klinik.',
        required: 'Påkrævet.',
        plainText: 'Brug almindelig tekst uden HTML-tags.',
        linkPair: 'Et link kræver både en tekst og en URL.',
        linkUrl: 'Angiv en fuld webadresse, f.eks. https://example.com',
        tooLong: 'Højst {max} tegn.',
        endAfterStart: 'Slutningen skal ligge efter starten.',
        endInPast: 'Slutningen er allerede passeret.',
      },
      back: 'Tilbage',
      next: 'Fortsæt',
      save: 'Gem',
      publish: 'Udgiv {count} meddelelse{{r}}',
      saveDraft: 'Gem kladde',
      created: '{count} meddelelse{{r}} oprettet',
      saved: 'Meddelelsen er gemt',
      failed: 'Meddelelsen kunne ikke gemmes.',
    },
  },
  specialitiesPage: {
    iconMissingFromWebKit:
      'Mangler i web-ikonsættet, så det vises ikke på desktop. Upload det til det Font Awesome-kit først.',
    iconMissingFromMobileKit:
      'Mangler i mobil-ikonsættet, så det vises ikke i Capture. Upload det til det Font Awesome-kit, og udgiv apps først.',
    back: 'Tilbage',
    info: 'Speciale',
    editConditionType: 'Rediger {name}',
    conditionTypeNameLocked:
      'Det engelske navn identificerer tilstandstypen over for apps og kan ikke ændres her.',
    conditionTypeCreated: 'Tilstandstypen blev oprettet.',
    conditionTypeUpdated: 'Tilstandstypen blev opdateret.',
    noConditionTypes: 'Ingen tilstandstyper endnu',
    notVisibleYet:
      'Specialet vises ikke i apps, før det har mindst én tilstandstype.',
    title: 'Specialer',
    addSpeciality: 'Tilføj speciale',
    editSpeciality: 'Rediger speciale',
    noSpecialities: 'Ingen specialer endnu',
    loadError: 'Specialerne kunne ikke indlæses. Prøv igen.',
    conditionTypesLoadError: 'Tilstandstyperne kunne ikke indlæses. Prøv igen.',
    noIcon: 'Intet ikon',
    positionBefore: '{position} — før {item}',
    positionLast: '{position} — sidst',
    positionPushesDown: '{item} og alt nedenunder rykker en ned.',
    positionAtEnd: 'Tilføjes sidst på listen.',
    conditionTypes: 'Tilstandstyper',
    addConditionType: 'Tilføj tilstandstype',
    createSuccess: 'Specialet blev oprettet.',
    updateSuccess: 'Specialet blev opdateret.',
    columns: {
      icon: 'Ikon',
      name: 'Navn',
      nameDa: 'Dansk navn',
      nameDe: 'Tysk navn',
      conditionTypes: 'Tilstandstyper',
      ordering: 'Placering',
    },
    fields: {
      name: 'Navn (engelsk)',
      nameDa: 'Navn (dansk)',
      nameDe: 'Navn (tysk)',
      icon: 'Ikon',
      iconPlaceholder: 'Vælg et ikon',
      ordering: 'Placering',
    },
    errors: {
      nameRequired: 'Indtast et navn',
      conditionTypeNameRequired: 'Indtast et navn på tilstandstypen',
      duplicateName: 'Navnet er allerede taget.',
      saveFailed: 'Specialet kunne ikke gemmes. Prøv igen.',
      conditionTypeSaveFailed: 'Tilstandstypen kunne ikke gemmes. Prøv igen.',
    },
  },
  federationPage: {
    pageTitle: 'Indstillinger',
    loadError: 'Organisationens indstillinger kunne ikke indlæses.',
    generalSettings: {
      title: 'Generelle indstillinger',
      locale: 'Sprog',
      localeDescription: 'Angiver standardsproget for denne organisation.',
      localePlaceholder: 'Vælg sprog',
      localeOptions: {
        en: 'Engelsk',
        da: 'Dansk',
        de: 'Tysk',
      },
      requireManualRelease: 'Aktiverer manuel frigivelse af sager',
      requireManualReleaseDescription:
        'Når aktiveret, skal sager manuelt frigives af administrativt personale, før de sendes til en specialist.',
      reassignmentHours: 'Viderefordeling af sag (timer)',
      reassignmentHoursDescription:
        'Angiver hvor mange timer der går, før en ubesvaret sag automatisk tildeles en anden specialist.',
      reassignmentHoursPlaceholder: 'F.eks. 12',
      referralNotificationEmail: 'Notifikations-e-mail for nye sager',
      referralNotificationEmailDescription:
        'E-mailadresse der modtager en notifikation, hver gang en ny sag oprettes i denne organisation.',
      referralNotificationEmailPlaceholder: 'email@eksempel.dk',
      claimReferral: 'Reservér sag',
      claimReferralDescription:
        'Giver specialister mulighed for at reservere en sag, før de svarer. Kan bruges når flere specialister arbejder i samme klinik, så to specialister ikke svarer samme sag.',
      hideEconsultIdentifier: 'Skjul sags-ID',
      hideEconsultIdentifierDescription:
        'Skjuler identifikatoren for en sag i brugergrænsefladen.',
      imageQualityAssessment: 'Vurdering af billedkvalitet',
      imageQualityAssessmentDescription:
        'Giver specialisten mulighed for at vurdere kvaliteten af de billeder, den henvisende læge har taget.',
      enableEconsultNotifications: 'Aktivér notifikationer for televurderinger',
      enableEconsultNotificationsDescription:
        'Aktiverer eller deaktiverer e-mailnotifikationer til henvisende læger, når en sag er vurderet af en specialist.',
      econsultQuestionSummary:
        'Årsag til anmodning om televurdering og/eller noter',
      econsultQuestionSummaryDescription:
        'Angiver om feltet for årsag til anmodning om televurdering eller noter er obligatorisk at udfylde for den henvisende læge.',
      lesions: {
        title: 'Læsioner',
        description:
          'Læsioner er altid aktiveret som standard. Dette flow kan ikke deaktiveres.',
      },
      rashes: {
        title: 'Øvrig hud',
        description:
          'Aktivér dette for at give brugere mulighed for at registrere øvrig hud og tilgå det fulde workflow for øvrig hud i Dermloop.',
      },
      priorSpecialistAssignmentLookbackDays:
        'Tilbagebliksperiode for specialisttildeling (dage)',
      priorSpecialistAssignmentLookbackDaysDescription:
        'Antal dage der kigges tilbage, når en ny sag oprettes for en patient. Systemet tjekker hvilken specialist der har besvaret tidligere sager for denne patient inden for perioden, for at sikre kontinuitet i behandlingen ved at tildele den samme specialist.',
      userAffiliationOccupationAndSpecialisation:
        'Aktivér stilling og speciale',
      userAffiliationOccupationAndSpecialisationDescription:
        'Viser brugerens stilling og speciale i patientens journal og i nogle tilfælde i højre sidebar.',
      showDatetimeOnTeleEvaluation:
        'Vis dato og tid for teledermatologisk vurdering',
      showDatetimeOnTeleEvaluationDescription:
        'Viser dato og tidspunkt for specialistens vurdering til den henvisende læge.',
      enableTeleAssessmentRating: 'Aktivér bedømmelse af televurderinger',
      enableTeleAssessmentRatingDescription:
        'Giver klinikere mulighed for, anonymt at bedømme kvaliteten af en specialists vurdering. Bedømmelser er synlige for organisationsadministratoren.',
      timeSpentReviewingTeleassessments:
        'Aktivér tidsforbrug for specialisternes vurdering',
      timeSpentReviewingTeleassessmentsDescription:
        'Specialister skal angive hvor meget tid de har brugt på gennemgang af hver vurdering.',
      federationNameInPdfTitle: 'Aktivér organisationsnavn i PDF-titel',
      federationNameInPdfTitleDescription:
        'Inkluderer organisationsnavnet i titlen på PDF-rapporter genereret fra patientens journal.',
      inactivityDeactivationDays: 'Dage før deaktivering ved inaktivitet',
      inactivityDeactivationDaysDescription:
        'Antal dage en bruger kan være inaktiv, før kontoen deaktiveres.',
    },
    patientDataSettings: {
      title: 'Patientdataindstillinger',
      patientIdentifier: 'Patientidentifikator',
      patientIdentifierDescription:
        'Definerer hvilken type patientidentifikator der bruges i denne organisation.',
      patientIdentifierPlaceholder: 'Vælg identifikator',
      patientIdentifierOptions: {
        ssn: 'CPR-nummer',
        ssnDescription: 'Bruger CPR-nummeret som primær patientidentifikator.',
        mrn: 'Journalnummer',
        mrnDescription:
          'Bruger journalnummeret som primær patientidentifikator.',
      },
      hidePatientIdentifier: 'Skjul patientidentifikator',
      hidePatientIdentifierDescription:
        'Skjuler patientidentifikatoren i brugergrænsefladen på tværs af alle visninger.',
      hidePatientName: 'Skjul patientnavn',
      hidePatientNameDescription:
        'Skjuler patientnavnet i brugergrænsefladen på tværs af alle visninger.',
      patientBlockPolicy: 'Patientblokkeringspolitik',
      patientBlockPolicyDescription:
        'Styrer hvordan patientdata uden for brugerens klinik håndteres.',
      patientBlockPolicyOptions: {
        noBlock: 'Ingen blokering',
        noBlockDescription:
          'Brugere kan tilgå patientjournaler uden for deres klinik uden begrænsninger.',
        softBlock: 'Blød blokering',
        softBlockDescription:
          'Brugere får en advarsel ved forsøg på at tilgå patientjournaler uden for deres klinik, men adgang er stadig tilladt.',
        block: 'Blokering',
        blockDescription:
          'Brugere er forhindret i at tilgå patientjournaler uden for deres klinik.',
      },
      patientAddressCapture: 'Patientadresseregistrering',
      patientAddressCaptureDescription:
        'Beder om patientens adresse og e-mail under scanner-registrering, hvis de mangler.',
      skinCheckHistoryQuestions: 'Spørgsmål om hudtjek-historik',
      skinCheckHistoryQuestionsDescription:
        'Stiller to obligatoriske læsionsspørgsmål i capture om patientens planlagte og tidligere hudtjek hos en læge.',
    },
    save: 'Gem',
    federationName: 'Navn',
    federationNameDescription: 'Navnet for denne organisation.',
    features: {
      title: 'Funktioner',
      productAnalytics: 'Produktanalyse',
      productAnalyticsDescription:
        'Sender produktanalyse-hændelser fra de apps, man er logget ind i, for alle i denne organisation.',
      enabledElsewhere:
        'Produktanalyse er også slået til et andet sted for dele af eller hele denne organisation. Når du slår denne fra, slår du ikke det fra.',
      loadError: 'Kunne ikke hente funktionerne.',
      toasts: {
        saved: 'Produktanalyse gemt',
        error: 'Kunne ikke gemme produktanalyse',
      },
    },
    toasts: {
      saved: 'Organisationsindstillinger gemt',
      error: 'Kunne ikke gemme organisationsindstillinger',
    },
  },
  buttons: {
    addAdditionalCriteria: 'Tilføj yderligere kriterier',
    approve: 'Godkend',
    backToCaseList: 'Tilbage til case-liste',
    clinicalImages: 'Overbliksbilleder',
    close: 'Close',
    closeUpImages: 'Nærbilleder',
    compare: 'Sammenlign',
    compareImages: 'Sammenlign billeder',
    copy: 'Kopiér',
    dermoscopicImages: 'Dermatoskopiske billeder',
    dismiss: 'Afslut',
    editOrTakeAction: 'Redigér/Ny handling',
    inviteUser: 'Invitér bruger',
    lesionInfo: 'Læsionsinfo',
    login: 'Log ind',
    loginAsAnotherUser: 'Log ind som en anden bruger',
    next: 'Næste',
    nextCase: 'Næste case',
    open: 'Åbn',
    overviewImages: 'Overbliksbilleder',
    patientInfo: 'Patientinformation',
    previous: 'Forrige',
    refresh: 'Opdater',
    remove: 'Fjern',
    removeEmail: 'Fjern {email}',
    resetFilters: 'Ryd filtre',
    saveChangesAndClose: 'Gem og luk',
    sendInvitation: 'Send invitation',
    sendTeleRequest: 'Anmod om televurdering',
    startAnnotating: 'Start annotering',
    unapprove: 'Afvis',
    updateProfile: 'Opdatér profil',
  },
  calendar: {
    rangeFromLabel: 'Fra',
    rangeToLabel: 'til',
  },
  changeRegionAlertDialog: {
    changeRegion: 'Skift Region',
    changeToEUMessage:
      'Det ser ud til, at du befinder dig i Europa, men bruger vores amerikanske website. Vil du skifte til Dermloop EU?',
    changeToUSMessage:
      'Det ser ud til, at du befinder dig i USA, men bruger vores europæiske website. Vil du skifte til Dermloop US?',
    stayInEU: 'Bliv i EU',
    stayInUS: 'Bliv i US',
    changeToEU: 'Skift til EU',
    changeToUS: 'Skift til US',
  },
  clinicalActions: {
    addComment: 'Tilføj kommentar',
    allowedClinicalActions: 'Tilladte kliniske handlinger',
    otherClinicalAction: 'Anden klinisk handling',
    requestNewImage: 'Anmod om nyt billede',
    suggestedClinicalAction: 'Anbefalet klinisk handling',
    rateFollowUpPriority: 'Opfølgningsprioritet',
    followUpPriorityTooltip:
      'Vælg den opfølgningsprioritet der bedst afspejler, hvor hurtigt patienten bør ses af egen læge for videre vurdering eller behandling. Valget afgør, hvordan epikrisens prioritet markeres i egen læges journalsystem.',

    followUpPriorityNoneLabel: 'Ingen',
    accessLevels: {
      basic: 'Basisadgang (kun Teledermatologi)',
      limited:
        'Almindelig adgang (Teledermatologi, Opfølgning, Afslutning, Ny billedregistrering)',
      full: 'Fuld adgang (alle handlinger inkl. Biopsi, Biopsi planlægges, Topikal behandling)',
    },
  },
  clinicalStatistics: {
    action: 'Handling',
    average: '{medicalUnitName} gennemsnit',
    clinicalAction: 'Klinisk handling',
    clinicalActions:
      '{type|{referralPhysical: Physical referrals, referralTele: Regular tele-referrals, referralTeleUrgent: Urgent tele-referrals, biopsy: Biopsies, plannedBiopsy: Planned biopsies, followUp: Follow-ups, dismissal: Dismissals, newImageRegistration: New image registration, topicalTreatment: Topical treatment, noAction: No action}}',
    clinicalStatistics: 'Klinisk statistik',
    diagnosticAccuracy: 'Diagnostisk præcision',
    diagnosticValues: 'Diagnostiske værdier',
    distributionOfClinicalActions:
      'Fordelingen af de kliniske handlinger, du har valgt for dine patienter i Dermloop, sammenlignet med andre',
    falseNegative: 'Falskt negative',
    falsePositive: 'Falskt positive',
    forAllTime: 'Altid',
    last12Months: 'Sidste 12 måneder',
    last4Weeks: 'Sidste 4 uger',
    last7Days: 'Sidste 7 dage',
    negativePredictiveValue: 'Negativ prædiktiv værdi',
    notEnoughData: 'Ikke nok brugere til at vise data anonymt',
    performanceRelatingTo: ' ',
    positivePredictiveValue: 'Positiv prædiktiv værdi',
    sensitivity: 'Sensitivitet',
    specificity: 'Specificitet',
    toolTips: {
      diagnosticAccuracy:
        'Hvor ofte man klassificerer læsioner korrekt mht. benign og malign',
      falseNegative:
        'Det antal maligne læsioner, du fejlagtigt klassificerede som benign',
      falsePositive:
        'Det antal benigne læsioner, du fejlagtigt klassificerede som maligne',
      negativePredictiveValue:
        'Andelen af læsioner, der blevet klassificeret som benigne, der virkelig var benigne',
      positivePredictiveValue:
        'Andelen af læsioner, der blevet klassificeret som maligne, der virkelig var maligne',
      sensitivity:
        'Hvor mange af de maligne læsioner, der blev klassificeret som maligne',
      specificity:
        'Hvor mange af de benigne læsioner, der blev klassificeret som benigne',
      totalLesions:
        'Kun læsioner registreret af dig, hvor en endelig diagnose er registreret i Dermloop (afslutninger, televurderinger og biopsier), vises her.',
      trueNegative:
        'Det antal benigne læsioner, du korrekt klassificerede som benigne',
      truePositive:
        'Det antal maligne læsioner, du korrekt klassificerede som maligne',
    },
    totalLesions: 'Samlet antal læsioner',
    trainingStatistics: 'Træningsstatistik',
    trueNegative: 'Sandt negative',
    truePositive: 'Sandt positive',
    you: 'Dig',
    yourAbilityToClassifyLesions:
      'Din evne til at klassificere læsioner som benigne eller maligne. Kun patienter som er registreret i Dermloop vises',
    yourMedicalUnitDistribution: 'Din medicinske enheds fordeling',
    yourPersonalMetrics: 'Dine personlige tal',
  },
  compareImages: {
    header: {
      back: 'Tilbage til patienten',
      title: 'Sammenlign billeder',
      nImages: '{count} billede{{count:r}}',
    },
    images: 'billede{{nrOfImages:r}}',
    imageType:
      '{imageType|{Overview: Overblik, Close up: Nærbillede, Clinical: Klinisk, Follow-up dermoscopy: Opfølgning dermatoskopisk, Dermoscopy: Dermatoskopisk}}',
    allImageTypes: 'Alle billedtyper',
    dragAndDrop: 'Træk og slip for at vise billede',
    gallery: {
      title: 'Galleri',
      filters: 'Filtre',
      close: 'Luk',
      open: 'Åbn',
      sortBy: 'Sortér efter',
      sortMostRecentFirst: 'Nyeste først',
      sortOldestFirst: 'Ældste først',
      dateOfRegistration: 'Registreringsdato',
      allDates: 'Alle',
    },
    lesionFilter: {
      selectedText: 'Valgte tilstande',
      availableItemsText: 'Alle tilstande',
      clearText: 'Ryd',
    },
    toolbar: {
      rotate: 'Rotér',
      done: 'Færdig',
    },
  },
  imageOverview: {
    flashOff: 'Vis med blitz',
    flashOn: 'Vis uden blitz',
    showMarker: 'Vis markering',
    hideMarker: 'Skjul markering',
    downloadImage: 'Download billede',
    nImages: '{count} billede{{count:r}}',
  },
  consentDialog: {
    dialogTitle: 'Godkend databehandleraftale',
    dialogDescription:
      "Før du kan fortsætte, skal databehandleraftalen (DBA'en) mellem klinikken og Melatech godkendes. Kun én person fra klinikken behøver godkende DBA'en. Tjek med din leder, hvis du er i tvivl, om du er bemyndiget til at godkende DBA'en på klinikkens vegne. Efter godkendelse sendes DBA'en som PDF i underskrevet version til klinikkens eBoks inden for 5 hverdage. Har du spørgsmål, kan du kontakte support via support@melatech.io eller tlf. +45 44 40 82 45.",
    dialogConsentChosen: 'Godkend valgte',
    dialogConsentAll: 'Godkend alle',
    dialogNameColumn: 'Navn',
    dialogLinkColumn: 'Link',
    dialogLinkText: 'Link til aftalen',
    consentError: 'Vælg venligst alle obligatoriske aftaler for at fortsætte.',
  },
  consent: {
    consentList: {
      loadError: 'Samtykkerne kunne ikke indlæses. Prøv igen.',
      noDataInTable: 'Ingen tilgængelige data',
      header: {
        name: 'Navn',
        description: 'Beskrivelse',
        mandatory: 'Obligatorisk',
      },
      title: 'Samtykker',
    },
    consentEdit: {
      consent: {
        consentNameLabel: 'Internt navn',
        consentNamePlaceholder: 'Indtast internt navn',
        consentDescriptionLabel: 'Intern beskrivelse',
        consentDescriptionPlaceholder: 'Indtast intern beskrivelse',
        consentMandatoryLabel: 'Obligatorisk',
        consentExternalConsentLabel: 'Ekstern samtykke',
        consentJGIdsLabel: 'Jurisdictional groups',
        consentTypeLabel: 'Samtykke type',
        consentTypeTriggerText: 'Vælg',
        consentTypes:
          '{type|{patient:Patient, medical_unit:Klinisk enhed, user: Bruger}}',
        consentJGTrigger: {
          availableItemsText: 'Jurisdictional groups',
          clearText: 'Ryd',
          emptyText: 'Ingen jurisdictional groups',
          placeholder: 'Søg',
          searchText: 'Søg jurisdictional group',
          selectedText: 'Valgte jurisdictional groups',
          triggerText: 'Vælg',
        },
      },
      revision: {
        revisionNameLabel: 'Revisionsnavn',
        revisionNamePlaceholder: 'Indtast revisionsnavn',
        revisionDescriptionLabel: 'Revisionsbeskrivelse',
        revisionDescriptionPlaceholder: 'Indtast revisionsbeskrivelse',
        revisionLongTextLabel: 'Revisionsindhold',
        revisionLongTextPlaceholder: 'Indtast revisionsindhold ...',
        validityHoursLabel: 'Hvor længe er samtykket gyldigt? (i timer)',
        validityHoursPlaceholder:
          'Antal timer som samtykket er gyldigt efter det er givet.',
      },
      cannotBeChanged: 'Kan ikke ændres',
      delete: 'Slet',
      newConsent: 'Nyt samtykke',
      onDeleteText: {
        cantDelete: 'Du skal fjerne samtykket fra alle jurisdictional groups.',
        deleteError: 'Kunne ikke slette samtykket.',
      },
      onSubmitText: {
        success: 'Samtykke gemt med succes',
        createError: 'Kunne ikke oprette samtykket',
        updateError: 'Kunne ikke opdatere samtykket',
        revisionError:
          'Samtykket er gemt, men revisionen kunne ikke gemmes. Gem igen for at tilføje den.',
      },
      submit: 'Gem',
    },
  },
  copyMedicalRecord: {
    registration: `Oprettelse af ny {type}`,
    skinType: 'hudtype',
    patientWithRash: `Patient {type} klager over hudlidelse`,
    patientWithLesion: `Patient {type} kendt med læsion`,
    and: 'og',
    dateConnector: 'd.',
    telediagnosis: 'Telediagnose',
    differentialDiagnosis: 'Differentialdiagnose',
    primaryDiagnosis: 'Primær diagnose',
    secondaryDiagnosis: 'Sekundær diagnose',
    tertiaryDiagnosis: 'Tertiær diagnose',
    tentativeDiagnosis: 'Tentativ diagnose',
    recommendedAction: 'Anbefalet handling',
    note: 'Note',
    evaluatedBy: 'Vurderet af',
    by: 'af',
    additionalDetails: 'Kommentarer',
    plan: 'Plan',
    conditionAge: {
      age: ' gennem nogle {age}.',
      always: ' siden fødsel/tidlig barndom.',
      otherEmpty: ' uden kendt varighed.',
      unknown: ' uden kendt varighed.',
      none: ' hvor varighed ikke er oplyst.',
    },
    medication: {
      no: 'Ingen nye medikamenter eller eksponeringer forud for hudlidensens debut.',
      yes: 'Beskriver nye ekspositioner, men de er ikke nærmere beskrevet.',
      unknown: 'Usikkerhed omkring forudgående ekspositioner.',
      reported:
        'Forud for hudlidelsens debut, har patienten været eksponeret for {text}.',
    },
    symptoms: {
      no: 'Ingen associerede symptomer.',
      yes: 'Patienten beskriver symptomer, men de er ikke defineret yderligere.',
      unknown: 'Det er uklart om læsionen har nogle ledsagesymptomer.',
      reported: 'Læsionen {text}.',
      otherSymptoms: 'Symptomer i form af {text}.',
      ulceration: 'er ulcereret',
      bleeding: 'bløder',
      itching: 'klør',
      soreness: 'er øm',
    },
    appearanceChanges: {
      no: 'Ingen ændringer i udseende.',
      yes: 'Læsionen har ændret sig, men hvordan er ikke beskrevet.',
      unknown: 'Det er usikkert om læsionen har ændret sig.',
      reported: 'Ændringer i form af {text}.',
      otherChanges: 'Ændringer i form af {text}.',
      age: {
        selected: 'Ændringerne har fundet sted over {age}.',
        other: 'Ændringerne har fundet sted over {age}.',
        unknown: 'Tidsrammen for ændringerne er ukendt.',
      },
    },
    priorLesionTreatment: {
      no: 'Læsionen er ikke tidligere behandlet.',
      yes: 'Læsionen har tidligere været behandlet, men behandlingen er ikke beskrevet yderligere.',
      unknown: 'Der er uklart, om læsionen tidligere har været behandlet.',
      reported: 'Læsionen har tidligere været behandlet med {text}.',
    },
    priorRashTreatment: {
      no: 'Er ikke tidligere forsøgt behandlet.',
      yes: 'Beskriver tidligere behandlingsforsøg, uklart med hvad.',
      unknown: 'Beskriver tidligere behandlingsforsøg, uklart med hvad.',
      reported: 'Er tidligere behandlet med {text}.',
    },
    formerSkinCancerAndImmunosuppressantUse: {
      yes_yes:
        'Tidligere tilfælde med {cancers} samt immunsupprimerende behandling med: {immuno}.',
      yes_unspecified:
        'Tidligere tilfælde med {cancers} samt tidligere immunsupprimerende behandling, der ikke er beskrevet yderligere.{immuno}',
      yes_no:
        'Tidligere tilfælde med {cancers}, men ingen tidligere immunsupprimerende behandling.{immuno}',
      yes_unknown:
        'Tidligere tilfælde med {cancers}, men det er uklart, om patienten tidligere har været i immunsupprimerende behandling.{immuno}',
      yes_unanswered: 'Tidligere tilfælde med {cancers}.{immuno}',

      unspecified_yes:
        'Der er tidligere tilfælde med hudkræft, men det er ikke beskrevet yderligere. Patienten har tidligere været i immunsupprimerende behandling med {immuno}.{cancers}',
      unspecified_unspecified:
        'Der er tidligere tilfælde med hudkræft og immunsupprimerende behandling, men det er ikke beskrevet yderligere.{cancers}{immuno}',
      unspecified_no:
        'Der er tidligere tilfælde med hudkræft, men det er ikke beskrevet yderligere. Ingen historik med immunsupprimerende behandling.{cancers}{immuno}',
      unspecified_unknown:
        'Der er tidligere tilfælde med hudkræft, men det er ikke beskrevet yderligere. Det er uklart, om patienten tidligere har været i immunsupprimerende behandling.{cancers}{immuno}',
      unspecified_unanswered:
        'Der er tidligere tilfælde med hudkræft, men det er ikke beskrevet yderligere. {cancers}{immuno}',

      no_yes:
        'Ingen tidligere tilfælde med hudkræft, men har tidligere været i immunsupprimerende behandling med: {immuno}.{cancers}',
      no_unspecified:
        'Ingen tidligere tilfælde med hudkræft, men har tidligere været i immunsupprimerende behandling som ikke er specificeret yderligere.{cancers}{immuno}',
      no_no:
        'Ingen tidligere tilfælde med hudkræft eller immunsupprimerende behandling.{cancers}{immuno}',
      no_unknown:
        'Ingen tidligere tilfælde med hudkræft, men det er uklart, om patienten tidligere har været i immunsupprimerende behandling.{cancers}{immuno}',
      no_unanswered: 'Ingen tidligere tilfælde med hudkræft.{cancers}{immuno}',

      unknown_yes:
        'Der er tvivl om patienten tidligere har haft hudkræft, men patienten har tidligere været i immunsupprimerende behandling med: {immuno}.{cancers}',
      unknown_unspecified:
        'Der er tvivl om patienten tidligere har haft hudkræft men har tidligere været i immunsupprimerende behandling, som dog ikke er beskrevet yderligere.{cancers}{immuno}',
      unknown_no:
        'Der er tvivl om patienten tidligere har haft hudkræft men har ikke været i immunsupprimerende behandling.{cancers}{immuno}',
      unknown_unknown:
        'Der er tvivl om patienten tidligere har haft hudkræft og været igennem immunsupprimerende behandling.{cancers}{immuno}',
      unknown_unanswered:
        'Der er tvivl om patienten tidligere har haft hudkræft.{cancers}{immuno}',

      unanswered_yes:
        'Patienten har tidligere været i immunsupprimerende behandling med {immuno}.{cancers}',
      unanswered_unspecified:
        'Patienten har tidligere været i immunsupprimerende behandling men det er ikke beskrevet yderligere.{cancers}{immuno}',
      unanswered_no:
        'Ingen historik med immunsupprimerende behandling.{cancers}{immuno}',
      unanswered_unknown:
        'Der er tvivl, om patienten tidligere har været i immunsupprimerende behandling.{cancers}{immuno}',
    },
    rashDiscomfortAndSystemicSymptoms: {
      yes_yes:
        'Patienten klager over lokalt ubehag i form af: {discomfort}. Derudover beskrives systemiske symptomer som {symptoms}.',
      yes_unspecified:
        'Patienten klager over lokalt ubehag i form af: {discomfort}. Derudover beskrives uspecifikke systemiske symptomer.{symptoms}',
      yes_no:
        'Patienten klager over lokalt ubehag i form af: {discomfort}, men der er ingen systemiske symptomer.{symptoms}',
      yes_unknown:
        'Patienten klager over lokalt ubehag i form af: {discomfort}, men det er uklart om der også er systemiske symptomer.{symptoms}',
      yes_unanswered:
        'Patienten klager over lokalt ubehag i form af: {discomfort}. Der er ingen oplysninger om systemiske symptomer.{symptoms}',

      unspecified_yes:
        'Patienten klager over uspecifikke lokale symptomer, og beskriver systemiske symptomer i form af {symptoms}.{discomfort}',
      unspecified_unspecified:
        'Patienten har uspecifikke klager over lokale og systemiske symptomer.{discomfort}{symptoms}',
      unspecified_no:
        'Patienten beskriver uspecifikke lokale symptomer, men afviser systemiske symptomer.{discomfort}{symptoms}',
      unspecified_unknown:
        'Patienten beskriver uspecifikke lokale symptomer, mens tilstedeværelsen af systemiske symptomer er uklart.{discomfort}{symptoms}',
      unspecified_unanswered:
        'Patienten beskriver uspecifikke lokale symptomer, men der forelægger ingen oplysninger om systemiske symptomer.{discomfort}{symptoms}',

      no_yes:
        'Ingen klager over lokalt ubehag men systemiske symptomer i form af: {symptoms}.{discomfort}',
      no_unspecified:
        'Ingen klager over lokalt ubehag men der beskrives uspecifikke systemiske symptomer.{discomfort}{symptoms}',
      no_no:
        'Ingen klager over lokalt ubehag eller systemiske symptomer.{discomfort}{symptoms}',
      no_unknown:
        'Ingen klager over lokalt ubehag, men det er uklart om der også er systemiske symptomer.{discomfort}{symptoms}',
      no_unanswered:
        'Ingen klager over lokalt ubehag. Der er ingen oplysninger om systemiske symptomer.{discomfort}{symptoms}',

      unknown_yes:
        'Patienten er uklar omkring lokale symptomer, men beskriver systemiske symptomer i form af: {symptoms}.{discomfort}',
      unknown_unspecified:
        'Patienten er uklar omkring lokale symptomer, men beskriver uspecifikke systemiske symptomer.{discomfort}{symptoms}',
      unknown_no:
        'Patienten er uklar omkring lokale symptomer, men beskriver ingen systemiske symptomer.{discomfort}{symptoms}',
      unknown_unknown:
        'Patienten er uklar omkring lokale og systemiske symptomer.{discomfort}{symptoms}',
      unknown_unanswered:
        'Patienten er uklar omkring lokale symptomer. Der er ingen oplysninger vedrørende systemiske symptomer.{discomfort}{symptoms}',

      unanswered_yes:
        'Ingen oplysninger om lokale symptomer, men patienten bekræfter systemiske symptomer i form af {symptoms}.{discomfort}',
      unanswered_unspecified:
        'Ingen oplysninger om lokale symptomer, men patienten beskriver uspecifikke systemiske symptomer.{discomfort}{symptoms}',
      unanswered_no:
        'Ingen oplysninger om lokale symptomer og patienten afviser systemiske symptomer.{discomfort}{symptoms}',
      unanswered_unknown:
        'Ingen oplysninger om lokale symptomer, og det er uklart om der er systemiske symptomer.{discomfort}{symptoms}',
    },
    rashReliefAndWorseningFactors: {
      yes_yes:
        'Hudlidelsen lindres af {reliefFactor}, men forværres af {worseningFactor}.',
      yes_unspecified:
        'Hudlidelsen lindres af {reliefFactor}, men forværres af uspecificerede faktorer.{worseningFactor}',
      yes_no:
        'Hudlidelsen lindres af {reliefFactor}, og der er ingen forværrende faktorer.{worseningFactor}',
      yes_unknown:
        'Hudlidelsen lindres af {reliefFactor}, og det er uklart, om der er forværrende faktorer.{worseningFactor}',
      yes_unanswered: 'Hudlidelsen lindres af {reliefFactor}.{worseningFactor}',

      unspecified_yes:
        'Der er beskrevet lindrende faktorer, men de er ikke specificeret yderligere. Forværres af {worseningFactor}.{reliefFactor}',
      unspecified_unspecified:
        'Der er beskrevet lindrende og provokerende faktorer, men de er ikke specificeret yderligere.{reliefFactor}{worseningFactor}',
      unspecified_no:
        'Der er beskrevet lindrende faktorer, men de er ikke specificeret yderligere. Ingen forværrende faktorer.{worseningFactor}{reliefFactor}',
      unspecified_unknown:
        'Der er beskrevet lindrende faktorer, men de er ikke specificeret yderligere, og det er uklart om der er nogle forværrende faktorer.{reliefFactor}{worseningFactor}',
      unspecified_unanswered:
        'Der er beskrevet lindrende faktorer, men de er ikke specificeret yderligere.{worseningFactor}{reliefFactor}',

      no_yes:
        'Ingen lindrende faktorer, men forværres af {worseningFactor}.{reliefFactor}',
      no_unspecified:
        'Ingen lindrende faktorer, men forværres af uspecificerede årsager.{reliefFactor}{worseningFactor}',
      no_no:
        'Ingen lindrende eller provokerende faktorer.{reliefFactor}{worseningFactor}',
      no_unknown:
        'Ingen lindrende faktorer, og det er uklart om der er forværrende faktorer.{reliefFactor}{worseningFactor}',
      no_unanswered: 'Ingen lindrende faktorer.{reliefFactor}{worseningFactor}',

      unknown_yes:
        'Der er uklart om der er lindrende faktorer, men hudlidensen forværres af {worseningFactor}.{reliefFactor}',
      unknown_unspecified:
        'Der er uklart om der er lindrende faktorer, men der er beskrevet forværrende faktorer som ikke er specificeret nærmere.{reliefFactor}{worseningFactor}',
      unknown_no:
        'Der er uklart om der er lindrende faktorer, men der er ingen forværrende faktorer.{reliefFactor}{worseningFactor}',
      unknown_unknown:
        'Det er uklart om der er lindrende eller provokerende faktorer.{reliefFactor}{worseningFactor}',
      unknown_unanswered:
        'Uklart om der er lindrende faktorer.{worseningFactor}{reliefFactor}',

      unanswered_yes:
        'Hudlidelsen forværres af {worseningFactor}.{reliefFactor}',
      unanswered_unspecified:
        'Hudlidelsen forværres af uspecificerede årsager.{reliefFactor}{worseningFactor}',
      unanswered_no:
        'Ingen provokerende faktorer.{reliefFactor}{worseningFactor}',
      unanswered_unknown:
        'Det er uklart, om der er forværrende faktorer.{reliefFactor}{worseningFactor}',
    },
  },
  demoPatientDialog: {
    gotIt: 'Forstået',
    learnMore: 'Lær mere',
    thisIsATest: 'Dette er en <>TEST<>',
    title: 'Dermloop Demotilstand',
    youAreEnteringDemoMode:
      'Bemærk, at du er ved at starte <>Dermloop Demotilstand.<>',
    youAreInDemoMode: 'Du er lige nu i <>Dermloop Demotilstand.<>',
  },
  descriptors: {
    accessExpiresOn: 'Adgang udløber d.',
    addAdditionalDiagnosis: 'Tilføj yderligere diagnose',
    added: 'Tilføjet',
    additionalComment: 'Yderligere kommentar',
    allCorrectness: 'Alle',
    allItemsHasBeenSelected: 'Alle valgt',
    anItemHasBeenSelected: '{numberOfItemsSelected} handling{{er}} valgt',
    annotate: 'Annotér',
    annotation: 'Annotering',
    annotationRequests: 'Annotationsanmodninger',
    annotations: 'Annoteringer',
    areas: 'Områder',
    assessedBy: 'Vurderet af',
    assessmentAction: 'Handling',
    assessmentDifficulty: 'Vurderingens sværhedsgrad',
    biopsyTypes: 'Biopsityper',
    classicCriteriaForDiagnosis:
      'Klassiske kriterier for diagnose {diagnosis} ⦁ Vælg flere',
    clinic: 'Dermloop Clinic',
    clinicalCase: 'Klinisk case',
    clinicalCases: 'Kliniske cases',
    clinicalImage: 'Overbliksbillede',
    clinicalImageDescription: 'Beskrivelse af overbliksbillede',
    clinicalImageEvaluation: 'Evaluering af overbliksbillede',
    clinicalRole: 'Klinisk rolle',
    clinician: 'Kliniker',
    closeUpImage: 'Nærbillede',
    closeUpImageDescription: 'Beskrivelse af nærbillede',
    closeUpImageEvaluation: 'Evaluering af nærbillede',
    comment: 'Kommentar',
    contacted: 'Gennemlæst',
    contactedBy: 'Gennemlæst af',
    draftCreatedBy: 'Kladde oprettet af',
    correctness: 'Korrekthed',
    correctnessDefaultTriggerText: 'Korrekthed: Alle',
    delete: 'Slet',
    dateOfReferral: 'Henvisningsdato',
    dermloop: 'Dermloop',
    dermoscopicCriteria: 'Dermatoskopiske kriterier',
    dermoscopicCriteriaNr: 'Dermatoskopisk kriterie {index}',
    dermoscopicImage: 'Dermatoskopisk billede',
    dermoscopicImageDescription: 'Beskrivelse af dermatoskopisk billede',
    dermoscopicImageEvaluation: 'Evaluering af dermatoskopisk billede',
    diagnosedBy: 'Diagnosticeret af',
    diagnoses: 'Diagnoser',
    diagnosis: 'Diagnose',
    diagnosisDifficulty: 'Diagnostisk sværhedsgrad',
    diagnostics: 'Diagnostik',
    diagnosticsDifficulty: 'Diagnostisk sværhedsgrad',
    differentialDiagnosis: 'Differentialdiagnose',
    differentialDiagnosisNr: 'Differentialdiagnose {diagnosisNumber}',
    difficulty: 'Sværhedsgrad',
    disabled: 'Deaktiveret',
    disposition: 'Disponering',
    editLocation: 'Redigér placering',
    editMedicalRecordLesion: 'Redigér læsion #{lesionId}',
    editMedicalRecordRash: 'Redigér hudlidelse #{conditionId}',
    email: 'E-mail',
    enabled: 'Aktiveret',
    evaluatedBy: 'Vurderet af',
    expires: 'Udløber',
    finalDiagnosis: 'Endelig diagnose',
    formerRatersPlural: 'Tidligere bedømmer{{nrOfRaters:e}}',
    hasFormerSkinCancer: 'Tidligere malignt melanom eller hudkræft',
    histopathologicalDiagnosis: 'Histopatologisk diagnose',
    histopathologicalEvaluation: 'Histopatologisk vurdering',
    history: 'Historik',
    ifNoDiagnosisRashes:
      'Eller vælg nedenstående mulighed, hvis der ikke er nogen tentativ diagnose',
    imageNr: '{imageNumber}/{totalImages} Billeder',
    imageQuality: 'Billedkvalitet',
    imageQualityAssessment: 'Vurdering af billedkvalitet',
    images: 'Billeder',
    lesionAge: 'Læsionens alder',
    lesionEditing: 'Læsionsredigering',
    lesionImagePathology: 'Histopatologisk vurdering',
    lesionImageTeledermatology: 'Televurdering',
    lesionLocation: 'Læsionens placering',
    lesionsPlural: 'Læsion{{nrOfLesions:er}}',
    location: 'Placering',
    medicalUnit: 'Klinisk enhed',
    medicalUnitInformation: 'Klinisk enhed info',
    medicalUnitRole: 'Rolle i klinisk enhed',
    myCases: 'Mine cases',
    myRecentRegisteredLesions: 'Mine seneste registreringer',
    name: 'Navn',
    newInvitation: 'Ny invitation',
    noDateSelected: 'Ingen dato valgt',
    noDiagnosis: 'Ingen diagnose',
    noFormerRaters: 'Ingen tidligere bedømmere',
    notAssessedYet: 'Ikke vurderet endnu',
    notContacted: 'Ikke gennemlæst',
    notOnSelectedRecord: 'Du er ikke længere på den valgte patient',
    occupation: 'Stilling',
    orSearchFromMostUsed: 'Eller vælg blandt mest brugte',
    otherObjectiveFindings: 'Øvrige objektive fund',
    overdueFollowUp: 'Overskredet opfølgning',
    overview: 'Overblik',
    overviewImage: 'Overbliksbillede',
    overviewImageDescription: 'Beskrivelse af overbliksbillede',
    overviewImageEvaluation: 'Evaluering af overbliksbillede',
    pathology: 'Biopterede læsioner',
    pathologyID: 'Materiale-ID',
    patientContacted: 'Gennemlæst svar',
    patientLibrary: 'Søg i patientbibliotek',
    personalInformation: 'Personlig information',
    previousTreatmentOrTrauma: 'Tidligere behandling',
    primaryDiagnosis: 'Primær diagnose',
    rashesPlural: 'Øvrige tilstande',
    rateClinicalImage: 'Vurdér overbliksbillede',
    rateCloseUpImage: 'Vurdér nærbillede',
    rateDermoscopicImage: 'Vurdér dermatoskopisk billede',
    rateImageQuality: 'Vurdér billedkvalitet',
    rateOverviewImage: 'Vurdér overbliksbillede',
    raters: 'Bedømmere (n=)',
    recommendations: 'Anbefalinger',
    recommendedClinicalAction: 'Anbefalet klinisk handling',
    referrer: 'Henvisende læge',
    referrersInfo: 'Henvisende læge',
    resultsNr: '{resultNumber} resultat{{er}}',
    resultsNrCapped: '{cap}+ resultater',
    resultsPlural: 'Resultat{{nrOfResults:er}}',
    reviewedBy: 'Vurderet af',
    searchForDiagnosis: 'Søg efter diagnose',
    secondaryDiagnosis: 'Sekundær diagnose',
    selectTool: 'Vælg værktøj',
    showLess: 'Vis færre',
    showMore: 'Vis mere',
    skinTypes: 'Hudtyper',
    specialisation: 'Speciale',
    specialityAndCondition: 'Speciale og tilstand',
    status: 'Status',
    stepIndex: 'Trin 0{stepIndex}',
    tele: 'Anmodninger om televurdering',
    teledermatologicalDiagnosis: 'Telediagnose',
    teleDermatologicalDiagnosis: 'Telediagnose',
    teleDermatologicalEvaluation: 'Televurdering',
    teleEvaluation: 'Televurdering',
    tentativeClinicalDiagnosis: 'Tentativ klinisk diagnose',
    tertiaryDiagnosis: 'Tertiær diagnose',
    type: 'Type',
    typeACriteria: 'Indtast et kriterie',
    unableToDetermine: 'Ude af stand til at afgøre',
    unitUsers: 'Enhedens brugere',
    userName: 'Brugernavn',
    usersPersonalInformation: 'Brugerens personlige information',
    yourPersonalInformation: 'Din personlige information',
    moreOptions: 'Valgmuligheder',
    edit: 'Redigér',
  },
  deviceLogin: {
    dialog: {
      title: 'Log ind i Dermloop på dit Dermkit',
      description:
        'Åbn Dermloop-appen på dit Dermkit, og scan QR-koden nedenfor for at logge ind.',
    },
    featureBanner: {
      title: 'Log ind i Dermloop med dit Dermkit med QR-kode',
      step1BeforeIcon: 'Klik på ikonet',
      step1AfterIcon: 'øverst til højre på din skærm.',
      step2:
        'Åbn Dermloop appen på dit Dermkit og <>tryk på den grønne knap<> på din loginskærm:',
      hostedLoginButton: 'Scan og log ind',
      step3:
        'Brug dit Dermkit til at scanne QR-koden på din computerskærm, og du bliver automatisk logget ind.',
    },
  },
  diagnosisToolbar: {
    addDiagnosis: 'Tilføj yderligere diagnose',
    dermatologyDiagnosis: 'Diagnose fra dermatolog',
    dermatologyDiagnosisNote: 'Note',
    diagnosisTypeTitle: 'Hvilken type diagnose vil du gerne tilføje?',
    differentialDiagnosis: 'Differentialdiagnose {count}',
    histoPathologicalDiagnosis: 'Histopatologisk diagnose',
    histopathologyDiagnosisNote: 'Note (fx patologirapport)',
    notePlaceholder: 'Skriv note...',
    secondaryDiagnosis: 'Sekundær diagnose',
    submitButton: 'Gem',
    tertiaryDiagnosis: 'Tertiær diagnose',
    title: 'Tilføj diagnose',
  },
  overdueFollowUpToolbar: {
    description:
      'Denne læsion er markeret til opfølgning. Den valgte opfølgningstid er overskredet, og læsionen er derfor forsinket.',
    stepOne:
      'For at fjerne markeringen "forsinket" skal du tilføje en ny klinisk handling for denne læsion.',
    stepTwo: 'Det gør du ved at klikke på',
    editOrTakeAction: '“Redigér/Ny handling”.',
  },
  displayNameYou: '{firstName} {lastName} {youString|{(you): (dig), *:}}',
  dropdownItems: {
    changeMedicalUnit: 'Skift klinisk enhed',
    logout: 'Log ud',
    profile: 'Profil',
    settings: 'Indstillinger',
    unitUsers: 'Enhedens brugere',
    smartPhrases: 'SmartPhrases',
  },
  errorMessages: {
    alreadyInvitedToMedicalUnit:
      'Brugeren er allerede inviteret til den kliniske enhed',
    duplicateSorId: 'Der findes allerede en klinisk enhed med dette SOR ID',
    errorOccurred: 'Der skete en fejl. Prøv venligst igen',
    errorOccurredUpdatingLesion:
      'Der opstod en fejl under opdateringen af læsionen',
    errorOccurredWhileLoadingMedicalRecord:
      'Noget gik galt under indlæsning af journalen. Prøv venligst igen.',
    errorOccurredWhileSaving: 'Der opstod en fejl under gemning',
    conditionNotFound:
      'Tilstanden kunne ikke findes. Den kan være blevet slettet.',
    errorWhileCreatingAssessment:
      'Der opstod en fejl under oprettelse vurderingen',
    inputCorrectInputPlease: 'Indtast venligst en korrekt e-mail',
    invalidCPR: 'Ugyldigt CPR',
    lesionNotInHealthRecord: 'Læsion matcher ikke patient',
    pleaseFillOutAllFields: 'Udfyld alle felter',
    thisFieldIsRequired: 'Dette felt er påkrævet',
    uIdNotFound: '{UID|{SSN:CPR-nr.,MRN:Journalnr.}} ikke fundet',
    youAreNotAuthorizedInAnyMedicalUnits:
      'Hej {name}, du er ikke autoriseret i nogen kliniske enheder.',
    youAreNotPermittedToAccessThisPatient:
      'Du har ikke adgang til denne patient',
  },
  fieldPlaceholders: {
    cpr: 'Søg efter CPR',
    cprOrPathologyID: 'Søg efter CPR eller materiale-ID',
    cprOrSpecimentID: 'Søg efter CPR eller materiale-ID',
    enterCommentHere: 'Indtast din kommentar her...',
    loading: 'Indlæser...',
    mrn: 'Søg efter mrn',
    mrnOrName: 'Søg efter mrn eller navn',
    cprOrName: 'Søg efter CPR eller navn',
    otherClinicalAction: 'Indtast anden klinisk handling her...',
    pathologyId: 'Søg efter materiale-ID',
    searchDiagnosis: 'Søg efter diagnose',
    searchForCriteria: 'Søg efter kriterier',
    searchLocation: 'Søg placering',
    selectMedicalUnit: 'Vælg klinisk enhed',
    selectOccupation: 'Vælg stilling',
    selectSpecialisation: 'Vælg speciale',
    typeHereToEnterDescription: 'Skriv her for at udfylde beskrivelse',
  },
  filters: {
    skinType: 'Type',
    dateOfReferral: {
      title: 'Henvisningsdato',
    },
    diagnoses: {
      availableDiagnosesText: 'Alle diagnoser',
      clearText: 'Ryd diagnoser',
      diagnosisSearchText: 'Søg efter diagnose',
      emptyText: 'Ingen diagnoser fundet',
      placeholder: 'Indtast diagnose',
      selectedDiagnosisText: 'Valgte diagnoser',
      title: 'Diagnose',
      withTabs: {
        clinicalTabText: 'Klinisk diagnose',
        finalTabText: 'Endelig diagnose',
        histopathologicalTabText: 'Histopatologisk diagnose',
        teleTabText: 'Telediagnose',
        tentativeTabText: 'Tentativ diagnose',
      },
    },
    actions: {
      title: 'Anbefalede handling',
      clearText: 'Ryd handling',
      availableActionsText: 'Alle handlinger',
      selectedActionsText: 'Valgte handlinger',
      placeholder: 'Handling',
      searchText: 'Søg for handlinger',
    },
    difficulty: {
      title: 'Sværhedsgrad',
    },
    location: {
      availableLocationText: 'Alle placeringer',
      clearText: 'Ryd placeringer',
      emptyText: 'Ingen placeringer fundet',
      locationSearchText: 'Søg efter placering',
      placeholder: 'Angiv placering',
      selectedLocationText: 'Valgte placeringer',
      title: 'Placering',
    },
    referralCreatedAfter: {
      title: 'Anmodning om televurdering oprettet efter',
    },
    referralCreatedBefore: {
      title: 'Anmodning om televurdering oprettet før',
    },
    search: {
      insuranceInfo: 'xxx',
    },
    status: {
      all: 'Alle',
      annotated: 'Annoteret',
      approved: 'Godkendt',
      assessed: 'Vurderet',
      canceled: 'Annulleret',
      closed: 'Lukket',
      newReferral: 'Ny anmodning om televurdering',
      pendingInformation: 'Afventer information',
      pending: 'Afventer',
      released: 'Frigivet',
      skipped: 'Sprunget over',
      title: 'Status',
    },
    marked: {
      title: 'Bogmærke',
      all: 'Alle televurderinger',
      marked: 'Markerede televurderinger',
    },
    multiSelect: {
      availableStatus: 'Tilgængelig(e)',
      clearText: 'Nulstil',
      selectedStatus: 'Valgt(e)',
      triggerText: 'Status',
    },
    resetFilter: 'Nulstil',
    jurisdictionalGroup: {
      title: '',
      searchText: '',
      searchPlaceholder: '',
      listTitle: '',
      selectedListTitle: '',
      noResultsText: '',
    },
    medicalUnit: {
      title: '',
      searchText: '',
      searchPlaceholder: '',
      listTitle: '',
      selectedListTitle: '',
      noResultsText: '',
    },
  },
  genders: {
    female: 'kvinde',
    male: 'mand',
  },
  recentRegistrations: {
    title: 'Mine registreringer',
    empty: 'Ingen seneste registreringer',
  },
  headerElements: {
    annotations: 'Annoteringer',
    biopsies: 'Biopsier',
    cases: 'Cases',
    home: 'Hjem',
    announcementBanner: {
      dismiss: 'Luk',
      minimise: 'Minimér',
      expandCritical: '1 kritisk meddelelse — udvid',
    },
    demoModeHeader: {
      dermloopDemoMode: 'Dermloop Demotilstand',
    },
    statistics: 'Statistik',
    teleReferrals: 'Anmodninger om televurdering',
  },
  informationFieldLabels: {
    email: 'E-mail',
    firstName: 'Fornavn',
    lastName: 'Efternavn',
    phoneNumber: 'Telefonnummer',
  },
  administrationReferralsTable: {
    title: 'Ubesvarede eKonsultationer',
    noEConsults: 'Ingen ubesvarede eKonsultationer i denne føderation',
    columns: {
      eConsultNumber: 'eKonsultation nr.',
      created: 'Oprettet',
      medicalUnit: 'Enhed',
      type: 'Type',
      draft: 'Kladde',
      released: 'Frigivet',
      releasedDate: 'Frigivet dato',
      releasedToAllowed: 'Frigivet til tilladte medlemmer',
      status: 'Status',
      assignment: 'Tildeling',
    },
    values: {
      tele: 'Tele',
      physical: 'Fysisk',
      unassigned: 'Ikke tildelt',
    },
    statuses: {
      open: 'Åben',
      pendingInformation: 'Afventer information',
      closed: 'Lukket',
      cancelled: 'Annulleret',
    },
    filters: {
      available: 'Tilgængelige',
      selected: 'Valgte',
      newestFirst: 'Nyeste først',
      oldestFirst: 'Ældste først',
      sortCreated: 'Sortér efter oprettet',
    },
    actions: {
      moreOptions: 'Flere muligheder',
      viewCase: 'Se sag',
      changeStatus: 'Skift status',
      release: 'Frigiv',
    },
    toast: {
      statusUpdated: 'Status opdateret',
      statusUpdateFailed: 'Kunne ikke opdatere status',
      released: 'eKonsultation frigivet',
      releaseFailed: 'Kunne ikke frigive eKonsultationen',
    },
  },
  insuranceInfoTable: {
    accNr: 'acc. No.',
    assessed: 'xxx',
    assign: 'xxx',
    assignEConsultTo: 'xxx',
    assignTo: 'xxx',
    cancelEConsult: 'xxx',
    cancelled: 'xxx',
    pendingInformation: 'Afventer information',
    closed: 'xxx',
    closeEConsult: 'xxx',
    dob: 'xxx',
    eConsultId: 'xxx',
    editInsuranceInfoToolbar: {
      assignedTo: 'xxx',
      changeStatus: 'xxx',
      dateFormat: 'xxx',
      dob: 'xxx',
      downloadPDF: 'xxx',
      resendFax: 'xxx',
      firstName: 'xxx',
      icd10Code: 'xxx',
      insuranceNumber: 'xxx',
      insurer: 'xxx',
      lastName: 'xxx',
      mrn: 'xxx',
      patientAddress: 'xxx',
      patientCity: 'xxx',
      patientState: 'xxx',
      patientZip: 'xxx',
      payer: 'xxx',
      placeholders: {
        dob: 'xxx',
        firstName: 'xxx',
        icd10Code: 'xxx',
        insuranceNumber: 'xxx',
        insurer: 'xxx',
        lastName: 'xxx',
        mrn: 'xxx',
        patientAddress: 'xxx',
        patientCity: 'xxx',
        patientState: 'xxx',
        patientZip: 'xxx',
        payer: 'xxx',
        question: 'xxx',
      },
      question: 'xxx',
      save: 'Gem',
      saveAndRelease: 'Gem og frigiv',
      title: 'xxx',
      validationMessages: {
        icd10Code: 'xxx',
        mrn: 'xxx',
        name: 'xxx',
        patientAddress: 'xxx',
        patientCity: 'xxx',
        patientState: 'xxx',
        patientZip: 'xxx',
        payer: 'xxx',
        question: 'xxx',
      },
    },
    exportCSV: 'Eksportér CSV',
    from: 'xxx',
    moreOptions: 'xxx',
    network: 'xxx',
    newReferral: 'xxx',
    noEConsults: 'xxx',
    patient: 'xxx',
    referral: 'xxx',
    released: 'xxx',
    searchSpecialistPlaceholder: 'xxx',
    specialist: 'xxx',
    status: 'xxx',
    suggestedSpecialist: 'xxx',
    title: 'xxx',
    toast: {
      errorCanceled: 'xxx',
      errorClosing: 'xxx',
      errorExportCSV: 'Kunne ikke hente CSV',
      errorOnReassignSpecialist: 'xxx',
      errorUnCanceling: 'xxx',
      successfullyCanceled: 'xxx',
      successfullyClosed: 'xxx',
      successfullyExportedCSV: 'CSV hentet',
      successfullyReassignedSpecialist: 'xxx',
      successfullyUnCanceled: 'xxx',
      errorMarkingPendingInformation:
        'Fejl ved markering af afventer information',
      successfullyMarkedPendingInformation: 'Afventer information markeret',
      errorSaving: 'xxx',
      successfullySavedInsuranceInfo: 'xxx',
    },
    unCancelEConsult: 'xxx',
    viewCaseDetails: 'xxx',
    superSeededText: 'Anmodning om televurdering er overskrevet',
    superSeededTooltip:
      'Anmodningen om televurdering er overskrevet af en nyere anmodning.',
  },
  invitationExpired: {
    title: 'Dermloop Desktop',
    subtitle: 'Invitationen er udløbet',
    messageWithEmail: 'Invitationen til (<>{email}<>) er udløbet.',
    messageWithoutEmail: 'Invitationen er udløbet.',
    contactSuperuser:
      'Kontakt din superbruger, hvis du har brug for adgang til Dermloop.',
  },
  macroscopicInspectionType:
    '(Obsolete) Eng: {type|{None: None, Assymetrical: Assymetrical, Irregular Border: Irregular Border, Several Colours: Several Colours, Diameter >6 mm: Diameter >6mm, Other: Other}}',
  medicalRecordValues: {
    age: '(time: {age})',
    appearanceChanges: '{triageInfo}',
    none: 'Ingen',
    notRegistered: 'Ikke registreret',
  },
  clinicalAssessmentToolbar: {
    missingImagesTitle: 'Manglende billeder!',
    missingImagesDescription:
      'De nødvendige billeder findes ikke på denne case. Vær venlig at tilføje dem fra mobil appen.',
  },
  pathologyAssessmentToolbar: {
    createAssessmentSuccess: 'Du lavede en histopatologisk vurdering',
    primaryDiagnosisErrorMessage: 'Du skal vælge en primær diagnose',
    diagnosisDifficultyErrorMessage: 'Du skal vælge en sværhedsgrad',
    breslowThicknessErrorMessage: 'Indsæt et gyldigt tal med max 1 decimal',
    breslowThicknessPlaceholder: 'Skriv Breslow-tykkelse her...',
    breslowThicknessTitle: 'Breslow-tykkelse (mm)',
    breslowThicknessUnknown: 'Tumor-tykkelse ukendt (Tx)',
    referredBy: 'Biopsi indsendt af',
  },
  patientPage: {
    markPatient: {
      successfullyBookmarkedCondition: 'Case markeret',
      successfullyRemovedBookmark: 'Fjernet markering fra case',
      mark: 'Markér',
      marked: 'Markeret',
    },
    alert: {
      cancel: 'Annullér',
      pleaseNoteNotContactedPatient:
        'Bemærk venligst, at patienten p.t. IKKE er markeret som kontaktet.',
      takeAction: 'Vælg Handling',
      takeNewClinicalAction: 'Vælg ny klinisk handling?',
      weAssumePatientIsContacted:
        'Når du vælger en ny klinisk handling antager vi, at du først har kontaktet patienten.',
    },
    jumpToToolbar: {
      completeDraft: 'Færdiggør kladde',
      saveAssessment: 'Gem vurdering',
      addDiagnosis: 'Tilføj diagnose',
      completeEConsult: 'Færdiggør televurdering',
      completePathology: 'Færdiggør patologi',
      reviewEConsult: 'Gennemgå televurdering',
      reviewPathology: 'Gennemgå patologi',
      completeFollowUp: 'Færdiggør opfølgning',
      clinicalCase: 'Klinisk case',
    },
    editContact: {
      showAll: 'Vis telefonnummer, email og adresse',
      hideAll: 'Skjul telefonnummer, email og adresse',
      showPhone: 'Vis telefonnummer',
      hidePhone: 'Skjul telefonnummer',
      showAddress: 'Vis email og adresse',
      hideAddress: 'Skjul email og adresse',
      missingInfo: 'Manglende kontaktoplysninger',
      addPhone: 'Klik her for at tilføje telefonnummer',
      addEmail: 'Klik her for at tilføje email',
      addAddress: 'Klik her for at tilføje adresse',
      editPhone: 'Rediger telefonnummer',
      editEmail: 'Rediger e-mail',
      editAddress: 'Rediger adresse',
      phoneNumber: 'Telefonnummer',
      address: 'Adresse',
      city: 'By',
      postalCode: 'Postnummer',
      email: 'Email',
      invalidEmail: 'Indtast en gyldig e-mailadresse',
      invalidPostalCode: 'Indtast et gyldigt postnummer',
      confirm: 'Bekræft',
      discard: 'Annullér',
      toastSuccess: 'Kontaktoplysninger opdateret',
      toastError: 'Kunne ikke opdatere kontaktoplysninger',
    },
  },
  plaintext: {
    additionalMedicalUnitRoles:
      'Øvrig{{nrOfRoles:e}} rolle{{nrOfRoles:r}} i klinisk enhed',
    all: 'Alle',
    contact: 'Kontakt',
    copiedFromDermloop: 'Kopieret fra Dermloop',
    inviteByEmail: 'Invitér via e-mail',
    linkToCondition: 'Link til tilstand',
    melatechsTeam: 'Melatechs hold',
    on: 'på',
    onceYouRemove: 'Når du fjerner',
    restoreAccessByInvitingAgain:
      'Du kan genoprette deres adgang ved at invitere brugeren igen.',
    roleToThisUser: 'roller til denne bruger.',
    theyWillNoLongerHaveAccessTo: 'har de ikke længere adgang til',
    thisInvitationExpires: 'Denne invitation udløber {date}',
    thisMedicalUnit: 'denne kliniske enhed.',
    toAddAdditional: 'for at tilføje flere',
    toRemoveThisUser: 'for at fjerne denne bruger',
    useDermloopMobileApp:
      'Brug Dermloop Capture til at registrere nye billeder',
    yearOld: 'årig',
    youHaveBeenInvitedTo: 'Du er inviteret til',
  },
  referralStatisticsPage: {
    dailyStatisticsMissing: 'Ingen daglig statistik-data tilgængelig',
    dailyStatisticsTitle: 'Daglig statistik',
    dailyStatisticsToDateLabel: 'To uger før...',
    jurisdictionalGroupNameColumn: 'Jurisdictional group',
    pending24HoursColumn: '24-48 timer',
    pending48HoursColumn: '48-96 timer',
    pending96HoursColumn: '>96 timer',
    title: 'Henvisningsstatistik',
    totalPendingColumn: 'Afventende i alt',
    totalPendingReferrals24HoursTitle: '24-48 timer',
    totalPendingReferrals24HoursTooltip:
      'Henvisninger, der er mere end 24 timer gamle og mindre end 48 timer gamle',
    totalPendingReferrals48HoursTitle: '48-96 timer',
    totalPendingReferrals48HoursTooltip:
      'Henvisninger, der er mere end 48 timer gamle og mindre end 94 timer gamle',
    totalPendingReferrals96HoursTitle: '>96 timer',
    totalPendingReferrals96HoursTooltip:
      'Henvisninger, der er mere end 96 timer gamle',
    totalPendingReferralsTitle: 'Afventende henvisninger i alt',
    totalPendingReferralsTooltip:
      'Samlet antal henvisninger, der afventer televurdering',
    valueCouldNotBeLoaded: 'Kunne ikke hentes',
  },

  seranoteButton: {
    new: 'Ny',
  },
  searchResults: {
    adjustMessage:
      'Prøv at justere din søgning eller filtrér for at finde det, du leder efter',
    noResults: 'Ingen resultater fundet',
  },
  smartPhrases: {
    diagnosisNotFound: 'Diagnose ikke fundet...',
    actionNotFound: 'Handlingen blev ikke fundet...',
    teleDialog: {
      updateHeader: 'Opdatér eksisterende SmartPhrase?',
      updateContent: 'Dette erstatter den eksisterende SmartPhrase for:',
      diagnosisAndAction: '{diagnosis} og {recommendedAction}.',
      currentSmartPhrase: 'Eksisterende SmartPhrase:',
      cancelButton: 'Annuller',
      updateButton: 'Opdatér',
    },
    changeAlert: {
      title: 'Erstat nuværende tekst med SmartPhrase?',
      content:
        'Du har foretaget ændringer i den nuværende smartphrase. Vil du beholde din nuværende tekst, eller erstatte den med smartphrasen for det nye valg?',
      keepButton: 'Behold min nuværende tekst',
      replaceButton: 'Erstat med SmartPhrase',
    },
    errorMessages: {
      actionRequired: 'Anbefalet handling er påkrævet',
      diagnosisRequired: 'Diagnose er påkrævet',
      phraseRequired: 'SmartPhrase er påkrævet',
    },
    toast: {
      errorUpdatingSmartPhrasesOption:
        'Noget gik galt, vi kunne ikke opdatere dine SmartPhrases indstillinger',
      successfullyUpdatedSmartPhraseOption:
        '{state|{enabled:Aktiverede, disabled:Deaktiverede}} SmartPhrases på din Dermloop konto  med success',
      errorDeletion: 'Fejl under sletning af SmartPhrase',
      successfullyDeleted: 'Slettede SmartPhrase med success',
      success: 'SmartPhrase oprettet',
      updated: 'SmartPhrase opdateret',
      error: 'Fejl ved oprettelse af SmartPhrase.',
      noSmartPhraseId:
        'Fejl under opdatering af SmartPhrase. Intet smart phrase ID blev fundet.',
    },
    smartPhrases: 'SmartPhrases',
    results: 'result{{nrOfImages:ater}}',
    subTitle:
      'Disse smartphrases indsættes automatisk i feltet "Kommentar", når du har angivet diagnose og anbefalet klinisk handling ifm. en televurdering.',
    toolbar: {
      diagnosisPickHeader: 'Vælg diagnose og anbefaling',
      diagnosisAndActionHeader: 'Diagnose og anbefaling',
      diagnosisStep: 'Diagnose',
      enterDiagnosis: 'Indtast diagnose',
      enterSmartPhrase: 'Indtast din SmartPhrase her...',
      addSmartPhrase: 'Tilføj SmartPhrase',
      recommendedAction: 'Anbefalede handling',
      editSmartPhrase: 'Redigér SmartPhrase',
      saveChanges: 'Gem ændringer',
      insertSmartPhrase: 'Indsæt SmartPhrase',
      clearNote: 'Ryd note',
      updateSmartPhrase: 'Opdatér SmartPhrase',
      smartPhraseSaved: 'SmartPhrase gemt',
      createSmartPhrase: 'Opret SmartPhrase',
      enterNoteHere: 'Indtast din kommentar her...',
      smartPhrase: 'SmartPhrase',
    },
    table: {
      delete: 'Slet',
      cancel: 'Annuller',
      deleteSmartPhrase: 'Slet SmartPhrase?',
      cantBeUndone: 'Denne handling kan ikke fortrydes.',
      diagnosis: 'Diagnose',
      recommendedAction: 'Anbefalede handling',
      smartPhrase: 'SmartPhrase',
      noSmartPhrases: 'Du har ingen SmartPhrases endnu.',
      createSmartPhrase: 'Opret SmartPhrase',
      searchPlaceholder: 'Søg efter SmartPhrase',
      smartPhrasesEnabled: 'SmartPhrases slået til',
    },
    tooltips: {
      addSmartPhrase:
        'Du skal vælge både en diagnose og en klinisk handling for at kunne oprette en SmartPhrase.',
      actionAlreadyExists:
        'SmartPhrase findes allerede for den valgte kombination.',
    },
  },
  conditionHistory: {
    actions: {
      addedNewDiagnosis: 'Ny diagnose',
    },
    title: {
      newDiagnosis:
        'Tilføjet ny {diagnosisType|{pathologicalDiagnosis:histopatologisk, dermatologicalDiagnosis:dermatologisk, *:}} diagnose',
      pathology: 'Histopatologisk vurdering',
      patientContacted: 'Gennemlæst svar',
      conditionDetails: '{type}s info ændret',
      teledermatology: 'Televurdering',
    },
  },
  conditionList: {
    testPatient: 'Testpatient',
  },
  conditionOverview: {
    addNewDiagnosis: 'Tilføj diagnose',
    sendToJournal: 'Send til journal',
    sendToJournalSuccess: 'Sendt til journal',
    sendToJournalError: 'Fejl ved afsendelse af journal, forsøg igen',
  },
  conditionPage: {
    backToPatientOverview: 'Tilbage til patientoversigt',
    collapseSidebar: 'Skjul sidepanel',
    patientSectionLabel: 'Patient',
    specialitySectionLabel: 'Speciale',
    locationSectionLabel: 'Placering',
    draftBadge: 'Kladde',
    editCase: 'Rediger sag',
    copyJournal: 'Kopiér journal',
    more: 'Mere',
    downloadAllPhotos: 'Download alle billeder',
    downloadingPhotos: 'Downloader billeder...',
    imagesPlaceholder: 'Billeder',
    medicalJournal: 'Journal',
    assessment: 'Vurdering',
    taskCompleted: 'Opgaven er løst',
    taskCompletedNext: 'Fortsæt til næste opgave.',
  },
  conditionResponse: {
    title: 'Speciallægesvar',
    sent: 'Svar sendt',
    sentToast: 'Dit svar er sendt til den henvisende læge',
    couldNotOpen: 'Svarformularen kunne ikke åbnes. Prøv igen.',
    noAssignedForm:
      'Der er ikke opsat en svarformular for denne tilstandstype, så henvisningen kan ikke besvares her. Kontakt support.',
  },
  conditionResponseReview: {
    title: 'Gennemlæs svaret',
    send: 'Markér som gennemlæst',
    sentToast: 'Sagen blev markeret som gennemlæst',
    reviewedBy: 'Gennemlæst af {name}',
    ratingTooltip:
      'Denne vurdering indsamles for at sikre, at speciallægens svar konsekvent er af høj kvalitet. Vurderingen vil ikke være synlig for speciallægen.',
  },
  moveConditionDialog: {
    title: 'Flyt case',
    description:
      'Flyt denne tilstand og alt, der hører til den, til en anden patient i samme netværk. Modtagerpatienten beholder sin egen identitet.',
    whatMoves: 'Hvad flyttes',
    conditionLabel: 'Tilstand',
    currentPatient: 'Nuværende patient',
    destinationPatient: 'Modtagerpatient',
    unnamedPatient: 'Patient uden navn',
    triages: 'Registreringer ({count})',
    detailsNoteTitle: 'Følger med tilstanden',
    detailsNote:
      'Tilstandsdetaljer, diagnoser, markører og billederne på disse registreringer følger automatisk med tilstanden.',
    createNewPatient: 'Opret ny patient',
    createPatientTitle: 'Opret modtagerpatienten',
    createPatientFromLookup:
      'Oplysningerne kommer fra CPR-registret og gemmes som vist.',
    registerNotFound:
      'CPR-registret har ingen person med dette id, så casen kan ikke flyttes hertil.',
    registerDead:
      'CPR-registret angiver personen som død, så casen kan ikke flyttes hertil.',
    registerFailed: 'CPR-registret kunne ikke nås. Gå tilbage, og prøv igen.',
    createPatientManually:
      'Der er ingen registeropslag her, så indtast modtagerpatientens oplysninger.',
    lookingUp: 'Slår id op...',
    selectGender: 'Vælg køn',
    createPatientNoteTitle: 'Her indtastes kun identiteten',
    createPatientNote:
      'Navn, id, fødselsdato og køn. Kontaktoplysninger og samtykker flyttes ikke med.',
    findPatient: 'Find modtagerpatienten',
    search: 'Søg',
    noPatientFound: 'Der findes ingen patient med dette id i dette netværk.',
    samePatient: 'Tilstanden tilhører allerede denne patient.',
    searchFailed: 'Søgningen fejlede. Prøv igen.',
    fields: {
      firstName: 'Fornavn',
      lastName: 'Efternavn',
      birthDate: 'Fødselsdato',
      gender: 'Køn',
    },
    confirmTitle: 'Bekræft flytning',
    confirmDescription:
      'Tilstanden, dens registreringer, detaljer og billeder vil tilhøre modtagerpatienten. Dette kan ikke fortrydes i appen.',
    confirmRenameDescription:
      'Dette er den nuværende patients eneste case, så der oprettes ingen ny patient. Den nuværende patient får identiteten vist her, og kontaktoplysninger og samtykker fjernes. Dette kan ikke fortrydes i appen.',
    sourceDeletedTitle: 'Den nuværende patient slettes',
    sourceDeletedNote:
      'Patienten har ingen andre cases. Navn, id, kontaktoplysninger og samtykker fjernes.',
    next: 'Næste',
    back: 'Tilbage',
    cancel: 'Annuller',
    move: 'Flyt case',
    moving: 'Flytter...',
    toastMessages: {
      moveSuccess: 'Tilstanden er flyttet',
      moveFailed: 'Tilstanden kunne ikke flyttes: {error}',
    },
  },
  conditionForm: {
    notAnswered: 'Ikke besvaret',
    mostUsedDiagnoses: 'Mest brugte',
    noSubmission: 'Ingen formularbesvarelse for denne tilstand',
    expandImage: 'Åbn {label} i fuld skærm',
    noPhotoCaptured: 'Der blev ikke taget et {label}-billede',
    previousImage: 'Forrige billede',
    nextImage: 'Næste billede',
    nImages: '{count} billede{{count:r}}',
    previousThumbnails: 'Forrige miniaturebilleder',
    nextThumbnails: 'Næste miniaturebilleder',
    closeImage: 'Luk billede',
    downloadImage: 'Download billede',
    showFlashImage: 'Vis med blitz',
    hideFlashImage: 'Vis uden blitz',
    showNoFlashImage: 'Vis uden blitz',
    hideNoFlashImage: 'Vis med blitz',
    showImageMarker: 'Vis markering',
    hideImageMarker: 'Skjul markering',
    compare: 'Sammenlign',
    compareImages: 'Sammenlign billeder',
    imageActionUnavailable: 'Ikke tilgængelig endnu',
    unsupportedWidget:
      'Denne formular indeholder et spørgsmål, som denne version ikke kan vise.',
    unsupportedLocationMap:
      'Denne version kan ikke vise den anatomivisning, formularen bruger.',
    notePlaceholder: 'Angiv flere detaljer her...',
    placeMarkerHint: 'Klik på illustrationen for at sætte markøren',
    editLocation: 'Redigér placering',
    saving: 'Gemmer...',
    savedAt: 'Gemt {time}',
    saveFailed: 'Ændringer ikke gemt',
    completeRegistration: 'Fuldfør registrering',
    conclude: 'Afslut',
    discardDraft: 'Kassér kladde',
    submissionIncomplete:
      'Nogle obligatoriske spørgsmål er endnu ikke besvaret.',
    imagesNeedCapture:
      'Et påkrævet billede mangler. Billeder kan kun tilføjes i Capture.',
    staleRevision:
      'En anden har gemt sagen, efter du begyndte at redigere. Genindlæs for at redigere den nyeste version.',
    reload: 'Genindlæs',
    changesSaved: 'Ændringer gemt',
    awaitingAnswer: 'Afventer svar',
    answered: 'Besvaret',
    teleReferral: 'Telehenvisning',
    saveChanges: 'Gem ændringer',
    saveAndSendTeleReferral: 'Gem og send telehenvisning',
    saveAndRecordActions: 'Gem og registrér {count} handling{{count:er}}',
    actionIncomplete:
      'Udfyld detaljerne for hver ny handling, eller fjern den.',
    clinicalActions: 'Kliniske handlinger',
    cancelled: 'Annulleret',
    closed: 'Lukket',
    recorded: 'Registreret',
    cancelReferral: 'Annullér henvisning',
    keepReferral: 'Behold henvisning',
    newClinicalAction: 'Ny klinisk handling',
    noNewAction: 'Ingen ny handling',
    anotherAction: 'Endnu en {action}',
    actionLimitTeleReferral:
      'En telehenvisning afventer allerede svar. Speciallægen vil se disse ændringer.',
    actionLimitDismissal: 'Sagens seneste handling afslutter den allerede.',
    actionLimitMixed: 'En gemning registrerer én type handling.',
    cancelReferralTitle: 'Annullér denne telehenvisning?',
    cancelReferralDescription:
      'Speciallægen vil ikke længere se den. Den annulleres, når du gemmer, og du kan fortryde indtil da.',
    undo: 'Fortryd',
    removeExistingAction: 'Fjern {label}',
    dropQueuedAction: 'Fjern {label} fra gemningen',
    saveAndCancelTeleReferral: 'Gem og annullér telehenvisning',
    saveAndRemoveActions: 'Gem og fjern {count} handling{{count:er}}',
    saveAndApplyChanges: 'Gem og anvend {count} ændring{{count:er}}',
    actionRequired: 'Tilføj en handling for at afslutte registreringen.',
    encounterRequired: 'Åbn sagen fra Epic for at afslutte registreringen.',
    actionsNotAllowed:
      'Disse handlinger kan ikke registreres sammen. Fortryd en af ændringerne.',
    actionSent: 'Sendt {date}',
    actionSentBy: 'Sendt {date} af {name}',
    actionTakenBy: '{date} af {name}',
    removedOnSave: 'Fjernes, når du gemmer',
    cancelledOnSave: 'Annulleres, når du gemmer',
  },
  patientDeviceLogin: {
    open: 'Åbn i Dermkit',
    close: 'Luk QR-koden',
    instruction: 'Åbn Dermkit på din telefon, og scan koden.',
    autoExpiry: 'Koden udløber automatisk efter scanning.',
    remaining: '{seconds} sekunder tilbage',
    loading: 'Opretter QR-kode',
    error: 'QR-koden kunne ikke oprettes.',
    retry: 'Prøv igen',
  },
  tableColumns: {
    latestActivityOn: 'Seneste aktivitet:',
    activityBy: 'af',
    referredOn: 'Henvist den:',
    tentativeShort: 'T:',
    finalShort: 'E:',
    referred: 'Henvist',
    on: 'Den:',
    by: 'Af:',
    latestActionAndStatus: 'Seneste handling og status',
    clinical: 'Klinisk:',
    clinicalShort: 'K:',
    tele: 'Tele:',
    teleShort: 'T:',
    histology: 'Histologi:',
    histologyShort: 'H:',
    internalClinician: 'Intern kliniker',
    externalClinician: 'Ekstern kliniker',
    released: 'Frigivet',
    unknownDate: 'Ukendt dato',
  },
  patientInfoPage: {
    backToCases: 'Tilbage til sager',
    chooseSpecialityTitle: 'Vælg et speciale for at fortsætte',
    chooseSpecialitySubtitle:
      'Åbner tilstandsvisningen med det valgte speciale.',
    registrationUnit: '{{count:registrering|registreringer}}',
    noRegistrationsYetTile: 'Ingen registreringer endnu',
    noSpecialitiesTitle: 'Ingen specialer registreret endnu',
    noSpecialitiesSubtitle:
      'Denne patient har ingen aktive specialeregistreringer. Se de seneste registreringer nedenfor.',
    specialitiesUnavailableTitle: 'Specialer kunne ikke indlæses',
    specialitiesUnavailableSubtitle:
      'Genindlæs siden for at prøve igen, eller åbn en nylig registrering nedenfor.',
    orOpenRecentRegistration: 'eller åbn en nylig registrering',
    columnRegistration: 'Registrering',
    columnDiagnoses: 'Diagnoser',
    columnActionAndStatus: 'Handling og status',
    columnLatestActivity: 'Seneste aktivitet',
    tentative: 'Tentativ:',
    final: 'Endelig:',
    awaitingEvaluation: 'Afventer vurdering',
    noRegistrations: 'Ingen registreringer endnu',
    registrationsUnavailable:
      'Registreringer kunne ikke indlæses. Genindlæs siden for at prøve igen.',
    openTasksLabel: 'Åbne opgaver',
    openTasksCount: 'Åbne opgaver: {count}',
    noOpenTasks: 'Ingen åbne opgaver',
    openTasksLoading: 'Indlæser åbne opgaver…',
    openTasksUnavailable: 'Åbne opgaver kunne ikke indlæses',
    taskEconsultsToReview: 'eKonsultationer til gennemsyn',
    taskDraftRegistrations: 'Kladderegistrering',
    taskOverdueFollowUps: 'Overskredet opfølgning',
  },
  statuses: {
    allStatuses: 'Alle statusser',
    annotated: 'Annoteret',
    approved: 'Godkendt',
    correct: 'Korrekt',
    incorrect: 'Ukorrekt',
    noFormerAnnotations: 'Ingen tidligere annoteringer',
    pending: 'Afventer',
    overdue: 'Overskredet',
    pendingInvitation: 'Invitation afventer',
    unspecified: 'Uspecificeret',
  },
  statusMessages: {
    anotherLesionBeingEdited: 'En anden læsion er ved at blive redigeret',
    biopsySuccessfullyClosed: 'Biopsi registreret',
    closeMenuToActivateButton:
      'Luk menuen til højre for at aktivere denne knap',
    copiedToClipboard: 'Kopieret til udklipsholder',
    diagnosisNotFound: 'Diagnose ikke fundet',
    invitationAccepted: 'Invitation accepteret',
    invitationRejected: 'Invitation afslået',
    invitationSentToEmail: 'Invitation sendt til {email}',
    lesionEditingDoesNotLiveHere: 'Læsionsredigering bor ikke længere her.',
    lesionNotPartOfAnyTask: 'Denne læsion er ikke en del af nogen opgave',
    lesionNotRegisteredForHistopathologicalEvaluation:
      'Denne læsion er ikke registreret til histopatologisk vurdering',
    lesionNotRegisteredForTeledermatologicalEvaluation:
      'Denne læsion er ikke registreret til teledermatologisk vurdering',
    mustBeValidPhoneNumber: 'Telefonnr. matcher ikke landekode',
    noClassicCriteriaAvailableForDiagnosis:
      'Ingen klassiske kriterier tilgængelige for diagnosen',
    noClassicCriteriaSelected: 'Ingen klassiske kriterier valgt',
    patientHasBeenContacted: 'Patienten er kontaktet',
    pleaseEnterPhoneNumber: 'Indtast telefonnr.',
    pleaseSelectADeparment: 'Vælg venligst en afdeling for at fortsætte',
    profileUpdated: 'Profil opdateret',
    required: 'Obligatorisk',
    selectLesionFromSidebar: 'Vælg venligst en læsion fra venstre sidepanel',
    somethingWentWrong: 'Noget gik galt',
    successfullySaved: 'Gemt',
    teleReferralRequestSent: 'Anmodning om televurdering sendt',
    teleReferralSuccessfullyClosed: 'Anmodning om televurdering sendt',
    triageSuccessfullyClosed: 'Registrering fuldført',
    undiagnosableDueToImageQuality: 'Diagnose ikke mulig pga. billedkvalitet',
    userHas14DaysToAccept:
      'Brugeren har 14 dage til at acceptere din invitation',
    userRemovedSuccessfully: 'Brugeren er blevet fjernet',
    userUpdated: 'Bruger opdateret',
    youAreAboutToLeave: 'Du er ved at forlade uden at have gemt',
  },
  teleAnswerRating: {
    title: 'Er svaret brugbart?',
    good: 'God',
    bad: 'Dårlig',
    tooltip:
      'Denne vurdering indsamles for at sikre, at den dermatologiske evaluering konsekvent er af høj kvalitet. Vurderingen vil ikke være synlig for teledermatologen.',
    goodPlaceholder: 'Hvad du synes var godt ved dette svar...',
    badPlaceholder: 'Hvilke punkter du mener kunne forbedres ved dette svar...',
    successfullyEvaluatedCase: 'Evaluerede sagen med success',
  },
  tasks: {
    tabOne: 'Egne opgaver',
    tabTwo: 'Klinikkens opgaver',
    action: 'Handling',
    noTasksHere: 'Du har løst din opgave!',
    continueToNextTask: 'Fortsæt til næste opgave, hvis der er flere tilbage.',
    annotationFeedback: {
      description:
        'Annoteringer, der er blevet gennemgået og har fået feedback',
      title: 'Annoteringsfeedback',
    },
    annotationsThatNeedReviews: {
      description: 'Annoteringer, der endnu ikke er gennemgået',
      title: 'Gennemgå annoteringer',
    },
    drafts: {
      description: 'Kladder, der endnu ikke er færdiggjort',
      title: 'Kladder',
    },
    biopsies: {
      description: 'Nye biopsisvar',
      title: 'Biopsier',
    },
    buttons: {
      start: 'Start',
      viewAll: 'Se alle',
    },
    menuItem: 'Opgaver',
    overdueFollowUps: {
      description:
        'Tilstande, der er registreret til opfølgning, og som skal ses',
      title: 'Overskredne opfølgninger',
    },
    pendingAnnotations: {
      description: 'Træningslæsioner, der endnu ikke er blevet annoteret',
      title: 'Afventer annoteringer',
    },
    teleAssessments: {
      description:
        'Ulæste televurderinger. Fjernes fra listen, når de markeres som gennemlæst.',
      title: 'Televurderinger',
    },
    title: 'Opgaver',
    total: 'I alt',
  },
  teledermatologicalAssessmentToolbar: {
    claimReferralButton: 'Hent',
    casesSkipped:
      'Sprang {count} utilgængelig{{|e}} {{sag|sager}} over i din liste',
    errorOccuredWhileClaimingReferral:
      'Fejl under hentning af anmodning om televurdering. Prøv igen.',
    referralClaimedByOtherUserDescription:
      'En anden speciallæge er allerede i gang med at besvare denne anmodning om televurdering.',
    referralNotAnswerableDescription:
      'Du har ikke adgang til at besvare denne anmodning om televurdering.',
    referralNotClaimedTitle: 'Anmodning om televurdering endnu ikke hentet',
    referredBy: 'Anmodning om televurdering indsendt af',
    videoLink: 'Klik her for at se en video om hvordan man tager gode billeder',
    videoDialogTitle: 'Hvordan man tager gode billeder',
    dermoscopicImageTitle: 'Dermatoskopisk billede',
    overviewImageTitle: 'Overbliks billede',
    commentTitle: 'Anbefaling om opfølgning hos egen læge',
    commentPlaceholder: 'Indtast din anbefaling her...',
    informReferrerAboutAcuteDiagnosis:
      'Informer henvisende læge om akut behandling',
    tooltips: {
      imageQualityRatingTitle:
        'Bruges til uddannelse af praktiserende læge og optimering af billedkvalitet i Dermloop.',
      teleDiagnosisTitle:
        'Primær og førstvalgte yderligere diagnose inkluderes i ambulantepikrise. Alle valgte diagnoser gemmes i Dermloop.',
      recommendationsTitle:
        'Angiv hvordan den henvisende læge skal håndtere læsionen. Valget indsættes i epikrisen, og hvis du anbefaler opfølgning hos egen læge, skal du vælge en prioritet, ellers kan prioritering springes over.',
      commentTitle:
        'Anbefaling til praktiserende læge. Uddannelsestekst, anbefalet behandling, uddybning af anbefaling m.m. Inkluderes i ambulantepikrise.',
    },
    timeSpentReviewing: 'Time spent reviewing',
    timeSpentReviewingDescription:
      "A minimum of five minutes reviewing the patient's record and preparing your clinical response is required to bill for CPT code 99451.",
    timeSpentReviewingPlaceholder: 'Time spent reviewing in minutes',
  },
  teleReferralTable: {
    columns: {
      eConsultIdNumber: 'Henv.nr.',
      status: 'Status',
      clinicalDiagnosis: 'Klinisk diagnose',
      teleDermatologicalDiagosis: 'Telediagnose',
      referrer: 'Henvisende læge',
      dateOfReferral: 'Henvisningsdato',
      assessedBy: 'Vurderet af',
      assignment: 'Tildelt',
      favourite: 'Favorit',
    },
    noData: 'Ingen anmodninger om televurdering fundet',
    placeholders: {
      primarySearchFieldPlaceHolderInsuranceFlow:
        'Søg efter henvisningsnummer, CPR-nr. eller navn',
      searchForMedicalUnit: 'Søg efter klinisk enhed',
    },
  },
  casesTable: {
    noData: 'Ingen cases fundet',
    tooltips: {
      cannotCombineFilters:
        'Filter kan ikke kombineres med den valgte mulighed.',
    },
  },
  tooltips: {
    clickToAddNewAreas: 'Klik på billedet for at tilføje nye områder',
    dragAndDropToDisplayImage: 'Træk og slip for at vise billede',
    editUser: 'Redigér bruger',
    notOnSameLesionAsEditing:
      '(Obsolete) Eng: Please note that you are not on the same lesion as you are editing',
    removeUser: 'Fjern bruger',
    userInformationIsMissing: 'Brugerinformation mangler',
    youNeedAtLeastOneAction: 'Vælg mindst én handling',
  },
  unitUsersPage: {
    pageTitle: 'Bruger administration',
    pageDescription: 'Se og redigér brugeroplysninger i ',
    multiple: 'Flere',
    none: 'Ingen',
    you: '(Dig)',
    deactivateUser: 'Deaktiver bruger',
    back: 'Tilbage',
    noData: 'Ingen data',
    deletedReasonExpired: 'Adgang udløbet',
    deletedReasonInactive: 'Inaktiv i {days} dage',
    deletedReasonInactiveGeneric: 'Grundet inaktivitet',
    deletedReasonRemovedByUser: 'Af superbruger',
    expired: 'pga. adgang udløbet',
    inactivity: 'pga. inaktivitet',
    vacationEndsIn: 'Tilbage fra ferie om {nrOfDays} dag{{e}}',
    vacationIn: 'Går på ferie om {nrOfDays} dag{{e}}',
    lastActiveToday: 'Sidst aktiv i dag',
    lastActiveDaysAgo: 'Sidst aktiv {nrOfDays} dag{{e}} siden',
    deactivatedIn: 'Deaktiveres om {nrOfDays} dag{{e}}',
    downloadCSVFailed: 'Kunne ikke downloade csv',
    downloadCSVSuccess: 'Download af csv lykkedes',
    usersPersonalInformation: 'Brugerens personlige information',
    firstName: 'Fornavn',
    lastName: 'Efternavn',
    email: 'E-mail',
    phoneNumber: 'Telefonnummer',
    npi: 'NPI',
    specialty: 'Speciale',
    update: 'Opdatér',
    updateNotAllowedHere:
      'Redigering er kun mulig i en afdeling, du administrerer. Skift afdeling for at redigere denne bruger.',
    userAffiliations: 'Brugertilknytninger',
    userAffiliationsDescription: 'Her er brugerens tilknytninger og roller',
    roles: 'Roller',
    telespecialist: 'Telespecialist',
    resendInvitation: 'Send invitation igen',
    copyInvitation: 'Kopiér invitation',
    deleteInvitation: 'Slet invitation',
    exportTrainingLeaderBoard: 'Eksportér rangliste',
    inactive: 'Deaktiveret',
    deactivatedOn: 'Deaktiveret',
    reactivateUser: 'Genaktiver bruger',
    reactivateUserSorRequired:
      'Kan ikke genaktivere bruger — SOR-registrering er påkrævet i denne føderation',
    invitations: 'Invitationer',
    noPendingInvitations: 'Ingen afventende invitationer',
    editAffiliation: 'Redigér tilknytning',
    editInvitation: 'Redigér invitation',
    expiryDateHelper: '* Brugeren har 90 dage til at acceptere din invitation',

    deactivateUserAlert: {
      deactivate: 'Deaktiver',
      deactivateUserAlertBulletPoint_1:
        'De vil fortsat kunne logge ind på deres konto og have adgang til andre kliniske enheder',
      deactivateUserAlertBulletPoint_2:
        'De vil fortsat fremgå af <>brugeroversigten for {medicalUnitName}<>',
      deactivateUserAlertBulletPoint_3:
        'Deres adgang til <>{medicalUnitName}<> kan gendannes når som helst',
      deactivateUserAlertTitle: 'Deaktiver bruger?',
      toastMessages: {
        deactivateFailure: 'Kunne ikke deaktivere bruger',
        deactivateSuccess: 'Brugeren med e-mail {email} er blevet deaktiveret',
      },
      willNoLongerHaveAccessTo:
        '<>{email}<> vil ikke længere have adgang til <>{medicalUnitName}<>',
    },

    toastText: {
      successfullyAddedUser: 'Du inviterede {email}',
      needEmailOrNPI: 'Udfyld e-mail og NPI',
      cancel: 'Luk',
      updatedUser: 'Du opdaterede brugeren',
      removedAffiliation: 'Du fjernede en rolle fra brugeren',
      somethingWentWrong: 'Noget gik galt',
      copiedLink: 'Kopierede invitationslink til din udklipsholder',
      deletedInvitation: 'Slettede invitationen',
      resendInvitation: 'Gensendte invitations e-mailen',
      tooManyInvitationReminders:
        'For mange mails sendt, du kan kun sende 1 i timen til den samme e-mail',
    },

    roleSheet: {
      title: 'Tilføj rolle',
      role: 'Rolle',
      network: 'Network',
      site: 'Site',
    },

    tableHeaders: {
      name: 'Efternavn, Fornavn',
      contact: 'Kontakt',
      association: 'Tilknytning',
      roles: 'Roller',
      role: 'Rolle',
      status: 'Aktivitetsstatus',
      added: 'Tilføjet',
      expiry: 'Udløber',
    },

    userRolesTableHeaders: {
      networkName: 'Network name',
      siteName: 'Site name',
      roles: 'Rolle(r)',
      occupation: 'Stilling',
      specialty: 'Speciale',
      conditionTypes: 'Tilstande',
      activityStatus: 'Aktivitetsstatus',
      added: 'Tilføjet',
    },

    conditionTypesField: {
      label: 'Tilstande',
      helperText: 'Lad stå tomt for at bruge klinikkens tilstande.',
      useClinicsConditionTypes: 'Brug klinikkens tilstande',
      inherited: 'Klinikkens tilstande — {names}',
      inheritedEmpty: 'Klinikkens tilstande — ingen tilgængelige',
      editFailed: 'Tilstandene kunne ikke gemmes',
      optionsSource: 'Kun tilstande, som {federation} tilbyder, kan vælges.',
      notOffered: '{name} (tilbydes ikke længere af {federation})',
      federationFallback: 'organisationen',
      loadError: 'Tilstandstyperne fra organisationen kunne ikke indlæses.',
    },

    filters: {
      searchForNameOrEmail: 'Søg efter navn eller e-mail',

      networkFilter: {
        title: 'Vælg netværk',
        allNetworks: 'Alle netværk',
        selectedNetworks: 'Valgte netværk',
        reset: 'Ryd',
        searchPlaceholder: 'Skriv et netværk...',
        defaultTriggerText: 'Alle netværk',
        selectedNetworksTriggerText: 'Netværk',
        noValues: 'Ingen netværk',
      },

      siteFilter: {
        title: 'Vælg sites',
        allSites: 'Alle sites',
        selectedSites: 'Valgte sites',
        reset: 'Ryd',
        searchPlaceholder: 'Skriv et site...',
        defaultTriggerText: 'Alle sites',
        selectedSitesTriggerText: 'Sites',
        noValues: 'Ingen sites',
      },

      statusFilter: {
        title: 'Vælg statuser',
        allStatuses: 'Alle statuser',
        selectedStatuses: 'Valgte statuser',
        reset: 'Ryd',
        selectedStatusesTriggerText: 'Aktivitets status',
        defaultTriggerText: 'Aktivitets status: Alle',
        noValues: 'Ingen status',

        values: {
          active: 'Aktiv',
          deactivated: 'Inaktiv',
          aboutToBeDeactivated: 'Deaktiveres snart',
          unaffiliated: 'Uden tilknytning',
          pendingInvitation: 'Invitation afventer',
          onVacation: 'Ferie',
          subValues: {
            unaffiliated: 'har aldrig hørt til en behandlingsenhed',
            active: 'aktiv inden for de sidste {days} dage',
            deactivated:
              'manuelt deaktiveret eller inaktiv i mere end {days} dage',
            aboutToBeDeactivated: 'inaktiv i {fromDays}-{toDays} dage',
          },
        },
      },

      userRolesFilter: {
        title: 'Vælg bruger roller',
        searchPlaceholder: 'Søg efter en rolle...',
        allRoles: 'Alle roler',
        selectedRoles: 'Valgte roler',
        reset: 'Ryd',
        selectedRolesTriggerText: 'Roler',
        defaultTriggerText: 'Roler: Alle',
        noResultsFound: 'Ingen roler fundet',
      },

      associationFilter: {
        title: 'Vælg tilknytninger',
        searchPlaceholder: 'Søg efter en klinisk enhed...',
        allAssociation: 'Alle tilknytninger',
        selectedAssociation: 'Valgte tilknytninger',
        reset: 'Ryd',
        selectedAssociationTriggerText: 'Tilknytninger',
        defaultTriggerText: 'Tilknytninger: Alle',
        noResultsFound: 'Ingen enheder fundet',
      },
    },

    expiringUsersFilter: {
      expiringSoonFilterName: 'Udløber: Om mindre end 14 dage',
      filterValue: 'Om mindre end 14 dage',
      noFilterValue: 'Alle brugere',
      notExpiringSoonFilterName: 'Udløber',
    },

    inviteUnitUserDialog: {
      sendInvitation: 'Send invitation',
      jurisdictionalGroup: 'Netværk',
      allowedClinicalActions: 'Kliniske handlinger',
      clinician: 'Kliniker',
      email: 'E-mail',
      expireDate: 'Udløbsdato',
      expireDateExplanation: 'obsolete',
      inviteUser: 'Invitér bruger',
      medicalUnit: 'Klinisk enhed',
      medicalUnitDisabled:
        'Denne klinik kan ikke vælges, da brugeren allerede har en rolle her.',
      npi: 'NPI',
      npiPlaceholder: 'NPI',
      occupation: 'Stilling',
      role: 'Rolle',
      specialty: 'Speciale',

      errorMessages: {
        invalidEmail: 'Indtast gyldig e-mail med @ og et .',
        invalidOccupation: 'Vælg en gyldig stilling',
        invalidSpecialty: 'Vælg et gyldigt speciale',
        noClinicalActionPicked: 'Vælg et sæt tilladte kliniske handlinger',
        noDatePicked: 'Vælg udløbsdato',
        noEmailEntered: 'Indtast e-mail',
        noOccupationPicked: 'Vælg stilling',
        noRolePicked: 'Vælg rolle',
        noSpecialtyPicked: 'Vælg speciale',
      },

      placeholders: {
        email: 'email@domain.com',
        medicalUnit: 'Vælg klinisk enhed',
        occupation: 'Vælg stilling',
        role: 'Vælg rolle',
        specialty: 'Vælg speciale',
      },
    },

    mergeUsersDialog: {
      title: 'Flet brugere',
      description:
        'Flyt identitetsfelter og tilknytninger fra én bruger over til hovedbrugeren. Brugeren, der migreres fra, deaktiveres bagefter.',
      selectUsers: 'Vælg brugere',
      selectTargetUser: 'Vælg hovedbruger...',
      selectSourceUser: 'Vælg bruger der migreres fra...',
      searchUsers: 'Søg brugere...',
      noUsersFound: 'Ingen brugere fundet',
      next: 'Næste',
      back: 'Tilbage',
      merge: 'Flet brugere',
      cancel: 'Annuller',
      sub: 'Sub (primær login-identifikator)',
      oneLoginSub: 'OneLogin Sub',
      kratosId: 'Kratos ID',
      fieldAlreadyMerged:
        'Dette felt er allerede blevet flettet fra en anden bruger',
      checkingActivity: 'Tjekker klinisk aktivitet...',
      loadingUsers: 'Indlæser brugerne...',
      sourceUserHasActivity:
        'Brugeren, der migreres fra, har klinisk aktivitet og kan ikke flettes. Sundhedsjournaler: {healthRecordCount}, Tilstande: {conditionCount}, Triageringer: {conditionTriageCount}',
      confirmMerge: 'Bekræft fletning',
      confirmMergeDescription:
        'Er du sikker på, at du vil flette disse brugere? Denne handling kan ikke nemt fortrydes.',
      merging: 'Fletter...',
      mainUser: 'Hovedbruger',
      userToMigrateFrom: 'Bruger der migreres fra',
      selectMainUserFirst: 'Vælg hovedbrugeren først',
      createdOn: 'Oprettet {date}',
      userIdShort: 'ID {id}',
      noMedicalUnit: 'Ingen behandlingsenhed',
      candidateRestrictionHint:
        'Kun brugere, der deler en behandlingsenhed med hovedbrugeren eller slet ikke tilhører en behandlingsenhed, kan migreres.',
      selectIdsToMigrate: 'Vælg de id’er der skal migreres',
      selectIdsToMigrateDescription:
        'Vælg de id’er, der skal flyttes fra brugeren til venstre over til hovedbrugeren til højre. Erstattede id’er streges over, og det id, der træder i stedet, vises med grønt. Du behøver ikke vælge nogen — tilknytningen flyttes under alle omstændigheder.',
      newValues: 'Nye værdier for hovedbrugeren',
      affiliationWarningTitle: 'Hvad flyttes med fletningen',
      affiliationWarning:
        'Den migrerede brugers tilknytning overføres til hovedbrugeren og lukkes i samme trin, så registreringen af den tilhører hovedbrugeren, uden at der gives ny adgang. Ikke-indsendte kladder bliver på den lukkede tilknytning i stedet for at flytte over i hovedbrugerens egen. Kun brugere fra en af hovedbrugerens behandlingsenheder, eller helt uden behandlingsenhed, kan migreres.',
      idTooltips: {
        sub: 'Den primære login-identifikator. Præfikset fortæller, hvilken loginmetode den hører til: dkid| er MitID, 1login| er 1Login, kratos| er Kratos og epic| er Epic.',
        oneLoginSub:
          '1Login-identifikatoren, der bevares ved siden af det primære login, så en 1Login-konto stadig er forbundet, efter brugeren er skiftet til MitID.',
        kratosId: 'Det identitets-id, som Kratos-loginflowet bruger.',
      },
      toastMessages: {
        mergeSuccess: 'Brugere blev flettet succesfuldt',
        mergeFailed: 'Kunne ikke flette brugere: {error}',
      },
    },
    reactivateUserDialog: {
      doNotSetExpirationDate: 'Indstil ikke udløbsdato',
      reactivate: 'Genaktiver',
      specifyExpirationDate:
        'Angiv udløbsdato for deres adgang til {medicalUnitName}',
      title: 'Genaktiver bruger?',
      toastMessages: {
        reactivateFailed:
          'Noget gik galt, brugeren er ikke blevet genaktiveret',
        reactivateSuccess:
          'Genaktiveringen lykkedes, og en ny invitation er blevet sendt til {email}',
      },
      willBeReactivatedWithPrevAccess:
        '<>{email}<> bliver genaktiveret med kliniker adgang til <>{medicalUnitName}<>',
    },
  },
  specialistGroupsPage: {
    pageTitle: 'Specialistgrupper',
    createNew: 'Opret ny',
    edit: 'Rediger specialistgruppe',
    back: 'Tilbage',
    searchGroups: 'Søg efter specialistgruppe',
    new: 'Ny specialistgruppe',
    info: 'Info om specialistgruppe',
    name: 'Navn',
    description: 'Beskrivelse',
    addName: 'Tilføj navn',
    addDescription: 'Tilføj beskrivelse af specialistgruppen',
    update: 'Opdatér',
    specialists: 'Specialister',
    searchSpecialists: 'Søg efter specialist eller klinik',
    noSpecialistGroups: 'Ingen specialistgrupper',
    addSpecialistGroup: 'Opret ny for at se dem listet her.',
    add: 'Tilføj',
    specialist: 'Specialist',
    type: 'Type',
    allTypes: 'Alle typer',
    email: 'E-mail',
    phoneNumber: 'Telefonnummer',
    affiliations: 'Tilknytninger',
    allAffiliations: 'Alle tilknytninger',
    noSpecialists: 'Ingen specialister i denne gruppe',
    addSpecialists: 'Tilføj specialister eller en klinik, og de vises her.',
    specialistGroupAffiliations: 'Specialistgruppens tilknytninger',
    searchAffiliations: 'Søg efter klinik',
    noAffiliations: 'Ingen tilknytninger fundet',
    addAffiliations:
      'Tilføj specialistgruppen til en eller flere klinikker eller netværk for at få dem vist her.',
    addSpecialist: 'Tilføj specialist',
    selectedSpecialists: 'Valgte specialister',
    clinicalSite: 'Klinik',
    network: 'Netværk',
    preferredSpecialists: 'Foretrukne specialister',
    allowedSpecialists: 'Tilladte specialister',
    openUserTable: 'Åbn i brugeroversigten',
    remove: 'Fjern fra listen',
    moreOptions: 'Valgmuligheder',
    nameAlreadyExists: 'Der findes allerede en specialistgruppe med dette navn',
    assignmentNotificationMode: 'E-mails ved ny tildeling',
    assignmentNotificationModeDescription:
      'Vælg, hvornår medlemmer får en e-mail om en nyligt tildelt sag.',
    assignmentNotificationModes: {
      EveryAssignment: {
        label: 'Ved hver tildeling',
        description: 'Send en e-mail til den tildelte specialist om hver sag.',
      },
      Standard: {
        label: 'Standard',
        description:
          'Send kun en e-mail, når specialisten ikke har andre ubesvarede sager og ikke har fået en e-mail inden for de seneste 2 timer.',
      },
      Off: {
        label: 'Fra',
        description:
          'Send aldrig e-mails om nye tildelinger. Påmindelser og varsler om omfordeling sendes stadig.',
      },
    },
    toasts: {
      created: 'Specialistgruppe oprettet',
      updated: 'Specialistgruppe opdateret',
      specialistAssigned: 'Specialist tilføjet til gruppen',
    },
  },
  medicalUnitsPage: {
    pageTitle: 'Klinikker',
    createNew: 'Opret ny',
    edit: 'Redigér klinik',
    back: 'Tilbage',
    new: 'Ny klinik',
    info: 'Klinikinformation',
    noSites: 'Ingen klinikker',
    addSites: 'Opret ny klinik for at se dem listet her.',
    searchSites: 'Søg efter klinik, ydernr eller CVR nr.',
    name: 'Navn',
    addName: 'Tilføj navn',
    jurisdictionalGroup: 'Netværk',
    addJurisdictionalGroup: 'Vælg netværk',
    conditionTypes: 'Tilstandstyper',
    conditionTypesCaption:
      'Viser de tilstandstyper, {federation} tilbyder. Kun Melatech kan ændre, hvilke typer en organisation tilbyder.',
    conditionTypeNotOffered:
      'Tilbydes ikke længere af {federation}, fjern markeringen for at fjerne den',
    federationFallback: 'organisationen',
    conditionTypesLoadError:
      'Tilstandstyperne fra organisationen kunne ikke indlæses.',
    defaultActions: 'Standardhandlinger for inviterede brugere',
    dermatoscope: 'Dermatoskop',
    description: 'Beskrivelse',
    addDescription: 'Tilføj beskrivelse',
    onboardingDate: 'Dato for onboarding',
    selectDate: 'Vælg dato',
    add: 'Tilføj',
    seeMore: 'Vis mere',
    seeLess: 'Vis mindre',
    otherDetails: {
      title: 'Adresse- og telefonoplysninger',
      address: 'Adresse',
      addAddress: 'Tilføj adresse',
      city: 'By',
      addCity: 'Tilføj by',
      state: 'Stat',
      addState: 'Tilføj stat',
      zipCode: 'Postnr',
      addZipCode: 'Tilføj postnr',
      phoneNumber: 'Telefon',
      addPhoneNumber: 'Tilføj telefonnr',
      fax: 'Fax',
      addFax: 'Tilføj fax',
      email: 'E-mail',
      addEmail: 'Tilføj e-mail',
    },
    workflows: 'Dermatology workflows',
    lesions: {
      title: 'Læsioner',
      description:
        'Læsioner er altid aktiveret som standard. Denne funktion kan ikke deaktiveres.',
    },
    rashes: {
      title: 'Øvrige tilstande',
      description:
        'Giver adgang til registrering af "øvrig hud", og det fulde workflow for "øvrig hud" i Dermloop.',
    },
    lesionThickness: {
      title: 'Brug læsionstykkelse',
      description:
        'Hvis slået til, vil klinikeren blive bedt om at estimere Breslow tykkelsen hvis melanom er valgt som tentative diagnose, under læsionsoprettelsen.',
    },
    xdis02Messages: {
      title: 'XDIS02: Epikriser',
      description:
        'Sender en epikrise til henvisende læges journalsystem via sundhedsdatanettet, når en specialist færdiggør en vurdering.',
    },
    xdis91Messages: {
      title: 'XDIS91: Korrespondancer',
      description:
        'Sender en korrespondance til brugerens journalsystem med kopi af patientens journal, når der klikkes på den særskilte knap i Dermloop-journalen.',
    },
    specialistManagement: {
      title: 'Specialisthåndtering',
      description:
        'Gør det muligt at konfigurere hvordan specialister håndtere sager på klinikniveau.',
    },
    providerId: 'Ydernr',
    locationCode: 'Lokationsnummer',
    cvr: 'CVR',
    created: 'Oprettet dato',
    noUsers: 'Ingen brugere på denne klinik',
    addUsers: 'Tilføj brugere for at se dem listet her.',
    update: 'Opdater',
    selectSpecialistGroup: 'Vælg specialistgruppe',
    tootlips: {
      allowedSpecialists:
        'Tilladte specialister er næste i rækken til at modtage en sag, hvis ingen foretrukne specialister svarer. De vil kun modtage sager efter den fulde rotation i den foretrukne gruppe er gennemført.',
      preferredSpecialists:
        'Foretrukne specialister er de første til at modtage en sag. Hvis ingen svarer, går sagen videre til gruppen af tilladte specialister.',
    },
    specialistsAlert: {
      title: 'Deaktivér specialiststyring?',
      description:
        'Hvis du deaktiverer specialiststyring, vil dine valgte specialistgrupper blive fjernet. Er du sikker på, at du vil fortsætte?',
      cancel: 'Annullér',
      confirm: 'Bekræft',
    },
    sorAlert: {
      title: 'Manglende SOR-ID',
      description:
        'En gyldig klinik skal have et SOR-ID. Er du sikker på, at du vil fortsætte uden en SOR-ID?',
      cancel: 'Annullér',
      confirm: 'Fortsæt',
    },
    deleteSorAlert: {
      title: 'Slet SOR-information?',
      description:
        'Er du sikker på, at du vil slette SOR-informationen fra denne klinik? Dette vil også fjerne ydernummeret.',
      cancel: 'Annullér',
      confirm: 'Slet',
    },
    toasts: {
      created: 'Klinik oprettet',
      updated: 'Klinik opdateret',
    },
  },
  jurisdictionalGroupPage: {
    pageTitle: 'Netværk',
    createNew: 'Opret ny',
    edit: 'Rediger netværk',
    back: 'Tilbage',
    new: 'Nyt netværk',
    info: 'Netværk info',
    noNetworks: 'Ingen netværk',
    addNetworks: 'Opret nyt netværk for at se dem her.',
    searchNetworks: 'Søg efter netværksnavn',
    name: 'Navn',
    addName: 'Navn på netværk',
    description: 'Beskrivelse',
    addDescription: 'Tilføj beskrivelse',
    insurancePayer: 'Forsikringsbetaler',
    insurancePayerDescription:
      'Liste over forsikringsselskaber tilknyttet netværket. Kun synlig for brugere med rollen som forsikringsadministrator.',
    insurancePayerName: 'Forsikringsselskab',
    newProvider: 'Tilføj forsikringsselskab',
    numberOfClinicalSites: 'Antal klinikker',
    numberOfUsers: 'Antal brugere',
    scanner: 'ID Scanner',
    fax: 'Fax',
    addFaxNumber: 'Tilføj faxnummer',
    openSite: 'Åbn afdeling',
    filters: {
      scannerFilter: {
        title: 'Scanner',
        defaultTriggerText: 'Alle scannere',
        selectedScannersTriggerText: 'Valgte scannere',
        reset: 'Nulstil',
        selectedScanners: 'Valgte',
        allScanners: 'Alle scannere',
        noValues: 'Ingen scannere',
        searchPlaceholder: 'Søg scannere',
      },
      createdOnFilter: 'Oprettet dato',
    },
    toasts: {
      created: 'Netværk oprettet',
      updated: 'Netværk opdateret',
    },
    scannerLabels: {
      ssn: 'CPR-scanner',
      noScanner: 'Manuel indtastning',
    },
    selfReactivation: 'Selvgenaktivering',
    selfReactivationDescription:
      'Tillader inaktive brugere at genaktivere deres egen konto.',
    trustedDomainMfaSkip: 'Spring telefonbekræftelse over',
    trustedDomainMfaSkipDescription:
      'Spring SMS-koden over for brugere, der logger ind med et link eller en kode sendt på e-mail.',
    emailDomainOptionsHint:
      'Disse indstillinger gælder kun for brugere, hvis e-mail har et tilladt domæne. Tilføj et domæne for at slå dem til.',
    allowedEmailDomain: 'Tilladt e-maildomæne',
    allowedEmailDomainPlaceholder: 'tilladt-domæne.dk',
    invalidEmailDomain: 'Ugyldigt format for e-maildomæne',
    addEmailDomain: 'Tilføj e-maildomæne',
    sendFaxOnReferralClosed:
      'Send fax når anmodning om televurdering lukkes af forsikringsadministrator',
    pdfDownloadEnabled: 'Vis knappen "Download PDF"',
    displayEligiblePayers: 'Vis godkendte forsikringsselskaber på forsiden',
    ehrManagedActionsEnabled: 'EHR-styrede handlinger',
    ehrManagedActionsEnabledDescription:
      'Når aktiveret, styres kliniske handlinger af det tilsluttede EHR-system (f.eks. Epic). Oprettelse af nye handlinger direkte i Dermloop er deaktiveret uden en encounterekontekst fra EHR-systemet.',
    save: 'Gem',
  },
  warningMessages: {
    areYouSureYouWantToDeleteAnnotation:
      'Er du sikker på, du vil slette annoteringen for {annotation}?',
    changesWillBeLost: 'Dine ændringer går tabt. Er du sikker?',
    lesionAlreadyHasAnApprovedAnnotation:
      'Denne træningslæsion har allerede en godkendt annotering. Fjern alle tidligere godkendelser og godkend denne annotering?',
    unsavedChanges:
      'Du har ugemte ændringer. Er du sikker på, du vil forlade siden?',
    unsavedChangesNavigation:
      'Du har ikke-gemte ændringer. Fuldfør eller fortryd dem for at fortsætte.',
    youAreAboutToUnapprove: 'Du er ved at afvise denne annotering. Fortsæt?',
  },

  emailOTP: {
    description: 'Vi har sendt en verifikationskode til: {email}',
    title: 'Verificér din e-mail',
    enterOTP: 'Indtast din verifikationskode',
    verify: 'Verificér',
    sendNewCode: 'Send ny kode',
    otpIsRequired: 'En valid verifikationskode er påkrævet',
    resetEmail: 'Skift e-mail',
    toasts: {
      emailResetSuccess:
        'Email nulstillet med success, indtast venligst din nye email',
      error: 'Noget gik galt',
      wrongCode: 'Ugyldig kode',
      success: 'E-mail verificeret',
      errorResending: 'Kunne ikke lave en ny verifikationskode',
      successResending: 'Sendte ny verifikationskode med success',
    },
  },

  vacation: {
    vacation: 'Ferie',
    helpMessage:
      'Angiv din ferieperiode her, så får du ikke tildelt sager i denne periode.',
    helpMessageAdmin:
      'Angiv brugerens ferieperiode her, så får de ikke tildelt sager i denne periode.',
    noVacations: 'Du har ikke angivet nogen ferie endnu.',
    from: 'Fra',
    until: 'Til',
    addVacation: 'Tilføj ferie',
    editVacation: 'Rediger ferie',
    deleteVacation: 'Slet ferie?',
    successfullyDeleted: 'Ferie slettet',
    errorDeletion: 'Fejl ved sletning af ferie',
    successfullyUpdated: 'Ferie opdateret',
    errorUpdating: 'Fejl ved opdatering af ferie',
    successfullyCreated: 'Ferie oprettet',
    errorCreating: 'Fejl ved oprettelse af ferie',
    fromDateRequired: 'Fra-dato er påkrævet',
    untilDateRequired: 'Til-dato er påkrævet',
    dates: 'Ferie datoer',
    selectDates: 'Vælg datoer...',
    datesRequired: 'Ferie datoer er påkrævet',
  },

  accountDelegation: {
    error:
      'Noget gik galt! Luk og åben dialogen eller kontakt support hvis problemet ikke forsvinder.',
    noValidExchangeToken: 'Kunne ikke åbne QR session. Prøv igen.',
    linkText: 'Åben i browseren',
    refreshTooltip: 'Klik for at generere ny QR-kode',
    refreshButtonText: 'Opdater QR-kode',
    generateNewQRCode: 'Generer ny QR-kode',
    or: 'eller',
    linkOnlyTitle: 'Velkommen til Dermloop',
    linkOnlyDescription:
      'Få adgang til din Dermloop-konto for at gennemgå patientcases.',
    linkOnlyButtonText: 'Åben Dermloop',
    linkOnlyFootnote: 'Dermloop åbner i en ny browserfane.',
    copiedToClipboard: 'Kopieret!',
  },

  conditionPdf: {
    title: '{name} rapport',
    conditionId: 'Case ID',
    patientId: 'CPR',
    name: 'Patientnavn',
    dob: 'Født',
    linkTitle: 'Se rapporten med billeder i fuld opløsning her:',
    pageCount: 'Side {currentPage} af {totalPages}',
    overviewImages: 'Overbliksbilleder',
    closeUpImages: 'Nærbilleder',
    dermoscopicImages: 'Dermatoskopiske billeder',
    images: 'Billeder',
    pcs: 'Stk.',
    seeAll: 'Se alle',
    yearOld: 'årig',
    success: 'Download af PDF lykkedes',
    button: {
      downloadPdf: 'Download PDF',
      downloadAll: 'Hele journalen',
      downloadLatest: 'Seneste televurdering',
    },
  },

  responseSidebar: {
    successfullyReviewed: 'Sagen blev markeret som gennemlæst',
    footer: {
      button: {
        notReviewed: 'Markér som gennemlæst',
        submitEvaluation: 'Indsend evaluering',
        reviewed: 'Gennemlæst',
      },
      description: {
        notReviewed: 'Gemmer din note og fjerner sagen fra din opgaveliste.',
        reviewed:
          'Sagen er markeret som gennemlæst og fjernet fra din opgaveliste.',
        followupQuestionSent:
          'Sagen er markeret som gennemlæst, da du har indsendt et opfølgende spørgsmål.',
      },
      notePlaceholder: {
        label: 'Tilføj note (valgfrit)',
        reviewed:
          'Sagen er markeret som gennemlæst og fjernet fra din opgaveliste.',
        followupQuestionSent:
          'Notefeltet er låst, da du har indsendt et opfølgende spørgsmål.',
        notReviewed: 'Skriv note til sagen i Dermloop...',
      },
    },
    responseReceipt: {
      disposition: 'Videre forløb',
      actionUnavailable: 'Denne handling tilbydes ikke længere i formularen',
      couldNotDisplay: 'Speciallægesvaret kunne ikke vises. Kontakt support.',
    },
    specialistResponse: {
      title: 'Specialist svar',
      note: 'Specialist note',
      evaluatedBy: 'Vurderet af',
      recommendation: 'Anbefaling',
      teleDiagnosis: 'Telediagnose',
      secondaryDiagnosis: 'Sekundær diagnose',
      tertiaryDiagnosis: 'Tertiær diagnose',
      differentialDiagnosis: 'Differentialdiagnose',
    },
    imageQualityAssessmentResponse: {
      videoLink:
        'Klik her for at se en video om hvordan man tager gode billeder',
      videoDialogTitle: 'Hvordan man tager gode billeder',
      dermoscopicImageVideoTitle: 'Dermatoskopisk billede',
      overviewImageVideoTitle: 'Overbliks billede',
      title: 'Vurdering af billedkvalitet',
    },
    errorMessages: {
      badEvaluationRequiresNote: 'En dårlig evaluering kræver en kommentar',
    },
    followUpQuestion: {
      title: 'Stil et opfølgende spørgsmål til specialisten.',
      placeholder:
        'Hvis du har et opfølgende spørgsmål til specialisten, kan du indtaste det her og indsende det ved at klikke på knappen nedenfor.',
      submitText: 'Send opfølgende spørgsmål',
      followUpQuestionSubmitted: 'Dit opfølgende spørgsmål er sendt',
      sent: {
        title: 'Dit opfølgende spørgsmål er nu sendt til specialisten.',
        question: 'Spørgsmål:',
        youWillBeNotified:
          'Du vil få besked i form af en ny televurdering, når specialisten har besvaret din henvendelse.',
      },
    },
    patientExplanation: {
      label: 'Patientvendt forklaring',
      description:
        'Teksten vises til patienten som forklaring og rådgivning til dermatologens vurdering. Hvis feltet er tomt, sendes ingen tekst.',
      placeholder: 'Indtast forklaring og rådgivning til patienten (valgfrit)',
    },
  },

  patientResponse: {
    supportedByDermloop: 'Understøttet af Dermloop',
    evaluationIntendedFor: 'Denne vurdering er beregnet til',
    dateOfAssessment: 'Vurderingsdato',
    dermatologistAssessment: 'Dermatologens vurdering',
    explanationAndAdvice: 'Forklaring og rådgivning',
    privacyAvailableUntil:
      'Af hensyn til dit privatliv er denne vurdering kun tilgængelig indtil:',
    questions: 'Spørgsmål?',
    contactMessage:
      'Hvis du har spørgsmål, kan du kontakte {organizationName}.',
    sentViaSecureLink: 'Sendt via et sikkert link',
    verifyToViewResults: 'Bekræft for at se dine resultater',
    enterIdentifierToAccess:
      'Indtast dit {identifierType|{ssn:CPR-nummer, phoneNumber:telefonnummer}} for sikkert at tilgå dine resultater',
    enterIdentifier:
      'Indtast {identifierType|{ssn:CPR-nummer, phoneNumber:telefonnummer}}',
    linkNoLongerActive: 'Dette link er ikke længere aktivt',
    linkExpiredMessage:
      'Dette link er udløbet for at beskytte dit privatliv og datasikkerhed',
    error: 'FEJL!',
    invalidIdentifier: 'Ugyldigt id. Kontrollér og prøv igen.',
    tooManyAttempts: 'For mange forsøg. Kontakt din klinik.',
    loading: 'Indlæser...',
    submit: 'Indsend',
  },
  support: {
    buttonTooltip: 'Kontakt support',
    title: 'Hvad har du på hjerte?',
    description: 'Fejl, spørgsmål, feedback eller idéer. Vi læser det hele.',
    step1Label: 'Skal casen med?',
    step2Label: 'Beskrivelse',
    step3Label: 'Hvordan vil du kontaktes?',
    descriptionPlaceholder:
      'Din henvendelse sendes sikkert, så du kan skrive det, der er nødvendigt for at vi kan hjælpe.',
    descriptionRequired: 'Skriv venligst hvad du har brug for hjælp til',
    switchToDetailed:
      'Gå til en case og åbn support igen for at sende casen med',
    notOnCasePage: 'Du har ikke en case åben lige nu',
    yes: 'Ja',
    no: 'Nej',
    contactMethodEmail: 'Email',
    contactMethodPhone: 'Telefon',
    emailLabel: 'Email adresse',
    emailPlaceholder: 'din.email@example.com',
    emailRequired: 'Angiv venligst en e-mailadresse',
    emailInvalid: 'Angiv venligst en gyldig e-mailadresse',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '+45 12 34 56 78',
    phoneRequired: 'Angiv venligst et telefonnummer',
    phoneInvalid: 'Angiv venligst et gyldigt telefonnummer',
    orContactDirectly: 'Eller kontakt os direkte',
    callCustomerSupport: 'Ring til os',
    callSupportDescription: 'Ring til os, så tager vi os af dig',
    phoneNumber: '+45 44 40 82 45',
    openingHours: 'Man-Fre 08:00-16:00',
    cancelButton: 'Annuller',
    sendRequestButton: 'Send henvendelse',
    confirmationTitle: 'Tak fordi du skrev!',
    ticketId: 'Ticket ID:',
    confirmationMessage: 'Vi vender tilbage hurtigst muligt.',
    updatesVia: 'Du hører fra os via',
    updatesOn: 'Du hører fra os på',
    closeButton: 'Luk',
    toastError:
      'Beskeden kunne ikke sendes — prøv venligst igen. Dermloop-teamet er blevet underrettet.',
  },
} satisfies NamespaceDesktopWebTranslation;

export default da_desktop_web;
